import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FaChevronDown, FaChevronRight, FaFutbol, FaBasketballBall, FaVolleyballBall, FaMapMarkerAlt, FaCheckCircle, FaTimesCircle, FaCcVisa, FaCcMastercard, FaCreditCard, FaArrowLeft, FaInfoCircle, FaUsers, FaPlus, FaTrash, FaClock, FaCalendarDay, FaSwimmingPool, FaEnvelope, FaIdCard, FaWallet, FaGraduationCap, FaRegMoneyBillAlt, FaDumbbell, FaSun } from 'react-icons/fa';
import { GiTennisRacket } from 'react-icons/gi';
import '../../styles/pages/sportsAppointment.scss';
import { getSportsRegionsWithFacilities, getSportsAvailableSlots, createSportsAppointment, verifySportsAppointment, getSportsAppointmentById, getGenderRestrictions, createClubApplication, verifyClubRegistration, getSportsAppointmentsByIdentity, requestSportsCancellation, confirmSportsCancellation, getPublicEducationFacilities } from '../../api/sportsAppointmentService';
import { initiatePayment, getPaymentResult } from '../../api/paymentService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../styles/pages/sportsAppointmentCancellation.scss';
import { formatCurrency } from '../../utils/formatters';
import TermsOfService from '../../components/Contracts/TermsOfService';
import RefundPolicy from '../../components/Contracts/RefundPolicy';
import DistanceSalesContract from '../../components/Contracts/DistanceSalesContract';
import CardApplicationForm from './components/CardApplicationForm';
import CardBalanceLoad from './components/CardBalanceLoad';
import PublicEducationRegistrationForm from './components/PublicEducationRegistrationForm';
import SummerSportsRegistrationForm from './components/SummerSportsRegistrationForm';
import TennisAcademyApplicationForm from './components/TennisAcademyApplicationForm';
import ClubPayment from './components/ClubPayment';

const facilityIcons = {
  Tenis: GiTennisRacket,
  Futbol: FaFutbol,
  Basketbol: FaBasketballBall,
  Voleybol: FaVolleyballBall,
  'Basketbol/Voleybol': FaBasketballBall,
  'Kapali Spor Havuzu': FaSwimmingPool,
  Fitness: FaDumbbell,
};

const facilityCoordinates = {
  'Rauf Denktas Tenis Kortlari': '36.587956, 36.146827',
  'Toki Millet Parki Tesisleri': '36.587563, 36.163551',
  'Millet Bahçesi Spor Tesisleri': '36.585065, 36.155411',
  'Mustafa Kemal Kapali Spor Salonu': '36.564500, 36.152073',
  'Denizciler Kapalı Spor Salonu': '36.564500, 36.152073'
};

const getBlockedSlotLabel = (slot) => {
  const source = (slot?.blockSource || '').toLowerCase();
  const reason = (slot?.blockReason || '').trim();
  const reasonLower = reason.toLowerCase();

  if (source === 'club') {
    return reason || 'Kulüp Rezervasyonu';
  }

  if (source === 'publiceducation' || source === 'publiceducationcourse') {
    return 'Halk Eğitim Kursu';
  }

  if (reasonLower.includes('halk egitim')) {
    return 'Halk Eğitim Kursu';
  }

  return reason || 'Bakım';
};
const TURKEY_TIMEZONE = 'Europe/Istanbul';

const getTurkeyDateString = (date = new Date()) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: TURKEY_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);

const addDaysToDateString = (dateString, days) => {
  const [year, month, day] = dateString.split('-').map(Number);
  const nextDate = new Date(Date.UTC(year, month - 1, day + days));
  return nextDate.toISOString().split('T')[0];
};

const getTurkeyLabelFromDateString = (dateString, formatOptions) => {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day, 12));

  return new Intl.DateTimeFormat('tr-TR', {
    timeZone: TURKEY_TIMEZONE,
    ...formatOptions
  }).format(date);
};

const getTurkeyCurrentHourMinute = () => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TURKEY_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(new Date());

  const hour = Number(parts.find((part) => part.type === 'hour')?.value || 0);
  const minute = Number(parts.find((part) => part.type === 'minute')?.value || 0);

  return { hour, minute };
};

const formatVatIncluded = (amount) =>
  `${Number(amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL (KDV dahil)`;

const initialAppointmentFormData = {
  firstName: '',
  lastName: '',
  idNumber: '',
  email: '',
  phone: '',
  userGender: '',
};

const SportsAppointment = () => {
  const [regions, setRegions] = useState([]);
  const [expandedRegions, setExpandedRegions] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [selectedSportType, setSelectedSportType] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => getTurkeyDateString());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);

  // Modes - Define all modes here to avoid ReferenceErrors
  const [isClubApplicationMode, setIsClubApplicationMode] = useState(false);
  const [isCancellationMode, setIsCancellationMode] = useState(false);
  const [isCardApplicationMode, setIsCardApplicationMode] = useState(false);
  const [isBalanceLoadMode, setIsBalanceLoadMode] = useState(false);
  const [isCardManagementMode, setIsCardManagementMode] = useState(false);
  const [cardManagementSubView, setCardManagementSubView] = useState('applications');
  const [clubManagementSubView, setClubManagementSubView] = useState('application');
  const [isPublicEducationMode, setIsPublicEducationMode] = useState(false);
  const [isSummerSportsMode, setIsSummerSportsMode] = useState(false);
  const [isTennisAcademyMode, setIsTennisAcademyMode] = useState(false);
  const [isClubPaymentMode, setIsClubPaymentMode] = useState(false);
  const [showMemberInfoModal, setShowMemberInfoModal] = useState(false);
  const [isSlotsLoading, setIsSlotsLoading] = useState(false);

  const [cancellationIdentity, setCancellationIdentity] = useState('');
  const [cancellationAppointments, setCancellationAppointments] = useState([]);
  const [cancellationSearched, setCancellationSearched] = useState(false);
  const [cancellationLoading, setCancellationLoading] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [cancellationSelectedAppointment, setCancellationSelectedAppointment] = useState(null);
  const [cancelVerificationCode, setCancelVerificationCode] = useState('');
  const [cancellationSubmitting, setCancellationSubmitting] = useState(false);

  const [formData, setFormData] = useState(initialAppointmentFormData);
  const [reservationType, setReservationType] = useState('Individual');
  const [personCount, setPersonCount] = useState(1);

  const [genderRestrictions, setGenderRestrictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const formRef = useRef(null);
  const paymentIframeRef = useRef(null);
  const latestSlotRequestKeyRef = useRef('');

  const handleTimeSlotSelected = (slot) => {
    setSelectedTimeSlot(slot);

    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  // Payment modal states
  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    cardName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: ''
  });

  const [paymentErrors, setPaymentErrors] = useState({});

  // Policy states
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsViewed, setTermsViewed] = useState(false);

  const [refundPolicyAccepted, setRefundPolicyAccepted] = useState(false);
  const [showRefundPolicyModal, setShowRefundPolicyModal] = useState(false);
  const [refundPolicyViewed, setRefundPolicyViewed] = useState(false);

  const [contractAccepted, setContractAccepted] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractViewed, setContractViewed] = useState(false);
  const [showProgramConfirmModal, setShowProgramConfirmModal] = useState(false);


  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingAppointmentId, setPendingAppointmentId] = useState(null);
  const [verificationError, setVerificationError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  // Club Application States
  const [showClubApplicationModal, setShowClubApplicationModal] = useState(false);
  const [clubApplicationData, setClubApplicationData] = useState({
    firstName: '',
    lastName: '',
    idNumber: '',
    taxNumber: '',
    email: '',
    phone: '',
    regionId: '',
    sportType: '',
    responsibleDays: []
  });
  const [clubApplicationLoading, setClubApplicationLoading] = useState(false);
  const [clubApplicationError, setClubApplicationError] = useState('');
  const [clubApplicationSuccess, setClubApplicationSuccess] = useState(false);
  // isClubApplicationMode moved to top
  const [clubSchedule, setClubSchedule] = useState({});
  const [isClubVerified, setIsClubVerified] = useState(false);
  const [clubVerifyData, setClubVerifyData] = useState({ taxNumber: '', phone: '' });
  const [verifyingClub, setVerifyingClub] = useState(false);

  // Appointment Flow State
  const [viewStep, setViewStep] = useState('slots'); // 'slots' | 'details' | 'confirmation' | 'payment' | '3dsecure'
  const [paymentHtml, setPaymentHtml] = useState(null);

  const isInIframe = (() => {
    try {
      return window.self !== window.top;
    } catch (err) {
      return true;
    }
  })();
  const apiOrigin = (() => {
    try {
      return new URL(process.env.REACT_APP_API_URL).origin;
    } catch (err) {
      return null;
    }
  })();

  const getParentOrigin = () => {
    try {
      return new URL(document.referrer).origin;
    } catch (err) {
      return window.location.origin;
    }
  };

  // Payment Result State
  const [paymentResultData, setPaymentResultData] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentOutcome, setPaymentOutcome] = useState(null); // 'success' | 'failed'
  const [pendingTransactionId, setPendingTransactionId] = useState(null);
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const [appointmentDetailsLoading, setAppointmentDetailsLoading] = useState(false);
  const [fitnessReservationResult, setFitnessReservationResult] = useState(null);

  const fetchAppointmentDetails = async (transactionId) => {
    if (!transactionId) return;
    setAppointmentDetailsLoading(true);

    try {
      const paymentResult = await getPaymentResult(transactionId);
      const appointmentId = paymentResult?.data?.appointmentId;
      if (appointmentId) {
        const appointmentResult = await getSportsAppointmentById(appointmentId);
        if (appointmentResult.isSuccess) {
          setAppointmentDetails(appointmentResult.data);
        }
      }
    } catch (err) {
      console.error('Randevu detayları alınamadı:', err);
    } finally {
      setAppointmentDetailsLoading(false);
    }
  };

  // Check URL parameters for payment callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('paymentStatus');
    const transactionId = params.get('transactionId');
    const errorParam = params.get('error');
    const pathname = window.location.pathname.toLowerCase();

    if (isInIframe) {
      let status = paymentStatus;
      if (!status) {
        if (pathname.includes('odeme-basarili')) {
          status = 'success';
        } else if (pathname.includes('odeme-basarisiz')) {
          status = 'failed';
        } else if (pathname.includes('/hizmet-rehberi/spor-randevu')) {
          status = 'failed';
        }
      }

      if (status) {
        window.parent.postMessage({
          type: 'PAYMENT_RESULT',
          status,
          transactionId,
          error: errorParam
        }, '*');
      }
      return;
    }

    if (paymentStatus === 'success' && transactionId) {
      setViewStep('3dsecure');
      setPaymentOutcome('success');
      setPaymentResultData({ transactionId });
      setPaymentError(null);
      setPaymentHtml(null);
      setPendingTransactionId(null);
      fetchAppointmentDetails(transactionId);
      // Clean URL without reload
      window.history.replaceState({}, '', '/hizmet-rehberi/spor-randevu');
    } else if (paymentStatus === 'failed') {
      setViewStep('payment');
      setPaymentOutcome('failed');
      setPaymentError(decodeURIComponent(errorParam || 'Ödeme başarısız oldu'));
      setPaymentResultData(null);
      setPaymentHtml(null);
      setPendingTransactionId(null);
      setAppointmentDetails(null);
      // Clean URL without reload
      window.history.replaceState({}, '', '/hizmet-rehberi/spor-randevu');
    }
  }, []);

  const handlePaymentIframeLoad = () => {
    const iframe = paymentIframeRef.current;
    if (!iframe) return;

    try {
      const href = iframe.contentWindow?.location?.href;
      if (!href) return;

      const iframeUrl = new URL(href);
      const normalizeHost = (host) => host.replace(/^www\./i, '');
      const sameHost = normalizeHost(iframeUrl.hostname) === normalizeHost(window.location.hostname);
      if (!sameHost) return;

      const paymentStatus = iframeUrl.searchParams.get('paymentStatus');
      const transactionId = iframeUrl.searchParams.get('transactionId');
      const error = iframeUrl.searchParams.get('error');

      let resolvedStatus = paymentStatus;
      if (!resolvedStatus) {
        const path = iframeUrl.pathname.toLowerCase();
        if (path.includes('odeme-basarili')) {
          resolvedStatus = 'success';
        } else if (path.includes('odeme-basarisiz')) {
          resolvedStatus = 'failed';
        } else if (path.includes('/hizmet-rehberi/spor-randevu')) {
          resolvedStatus = 'failed';
        }
      }

      if (resolvedStatus === 'success' && transactionId) {
        setViewStep('3dsecure');
        setPaymentOutcome('success');
        setPaymentResultData({ transactionId });
        setPaymentError(null);
        setPaymentHtml(null);
        setPendingTransactionId(null);
        fetchAppointmentDetails(transactionId);
      } else if (resolvedStatus === 'failed') {
        setViewStep('payment');
        setPaymentOutcome('failed');
        setPaymentError(decodeURIComponent(error || 'Ödeme başarısız oldu'));
        setPaymentResultData(null);
        setPaymentHtml(null);
        setPendingTransactionId(null);
        setAppointmentDetails(null);
      }
    } catch (err) {
      // Cross-origin iframe navigation; ignore until it returns to our origin
    }
  };

  useEffect(() => {
    const handleMessage = (event) => {
      const normalizeHost = (host) => host.replace(/^www\./i, '').replace(/^api\./i, '');
      const isSameOrigin = event.origin === window.location.origin;
      const isApiOrigin = apiOrigin && event.origin === apiOrigin;
      let isSameHost = false;
      if (!isSameOrigin && !isApiOrigin) {
        try {
          isSameHost = normalizeHost(new URL(event.origin).hostname) === normalizeHost(window.location.hostname);
        } catch (err) {
          isSameHost = false;
        }
      }
      if (!isSameOrigin && !isApiOrigin && !isSameHost) return;
      const data = event.data;
      if (!data || data.type !== 'PAYMENT_RESULT') return;

      if (data.status === 'success') {
        setViewStep('3dsecure');
        setPaymentOutcome('success');
        setPaymentResultData({ transactionId: data.transactionId });
        setPaymentError(null);
        setPaymentHtml(null);
        setPendingTransactionId(null);
        fetchAppointmentDetails(data.transactionId);
      } else if (data.status === 'failed') {
        setViewStep('payment');
        setPaymentOutcome('failed');
        setPaymentResultData(data.transactionId ? { transactionId: data.transactionId } : null);
        setPaymentError(decodeURIComponent(data.error || 'Ödeme başarısız oldu'));
        setPaymentHtml(null);
        setPendingTransactionId(null);
        setAppointmentDetails(null);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    fetchRegionsAndFacilities();
  }, []);

  useEffect(() => {
    if (!pendingTransactionId || paymentOutcome) {
      return;
    }

    let isCancelled = false;
    let attempts = 0;
    const maxAttempts = 40; // ~2 minutes with 3s interval

    const pollResult = async () => {
      if (isCancelled) return;
      attempts += 1;

      try {
        const result = await getPaymentResult(pendingTransactionId);
        const status = (result?.data?.status || '').toLowerCase();

        if (status === 'approved') {
          setViewStep('3dsecure');
          setPaymentOutcome('success');
          setPaymentResultData({ transactionId: pendingTransactionId });
          setPaymentError(null);
          setPaymentHtml(null);
          setPendingTransactionId(null);
          fetchAppointmentDetails(pendingTransactionId);
          return;
        }

        if (status === 'declined' || status === 'failed') {
          setViewStep('payment');
          setPaymentOutcome('failed');
          setPaymentResultData({ transactionId: pendingTransactionId });
          setPaymentError(result?.data?.message || 'Ödeme başarısız oldu');
          setPaymentHtml(null);
          setPendingTransactionId(null);
          setAppointmentDetails(null);
          return;
        }

        if (attempts >= maxAttempts) {
          setPaymentError('Ödeme sonucu alınamadı. Lütfen tekrar deneyin.');
          setPendingTransactionId(null);
        }
      } catch (err) {
        if (attempts >= maxAttempts) {
          setPaymentError('Ödeme sonucu alınamadı. Lütfen tekrar deneyin.');
          setPendingTransactionId(null);
        }
      }
    };

    pollResult();
    const intervalId = setInterval(pollResult, 3000);

    return () => {
      isCancelled = true;
      clearInterval(intervalId);
    };
  }, [pendingTransactionId, paymentOutcome]);

  useEffect(() => {
    if (selectedFacility) {
      fetchAvailableTimeSlots(selectedFacility.id, selectedDate, selectedSportType?.typeId);
    }
  }, [selectedDate, selectedFacility?.id, selectedSportType?.typeId, selectedFacility?.name, selectedFacility?.subName]);

  const fetchRegionsAndFacilities = async () => {
    try {
      setLoading(true);
      // Fetch facilities directly from SQL database via public education endpoint
      const response = await getPublicEducationFacilities();
      if (response.isSuccess) {
        const facilities = response.data || [];

        // Group individual facilities by their Region Name (Tesis Kompleksi)
        const grouped = {};
        facilities.forEach(facility => {
          // Use regionName as the header (e.g., "Rauf Denktaş Tenis Kortları")
          // Fallback to facilityName if regionName is missing
          const rName = facility.regionName || facility.facilityName;

          // Use regionId for stable grouping, or fallback to name if not provided
          const rId = facility.regionId || rName;

          if (!grouped[rId]) {
            grouped[rId] = {
              id: rId,
              name: rName,
              facilities: []
            };
          }

          // Add this specific facility/court to the region's facilities list
          grouped[rId].facilities.push({
            id: facility.facilityId,
            name: facility.facilityName, // Display name (e.g., "1. Kort")
            type: facility.typeName,
            regionName: rName,
            availableSports: facility.availableSports || [],
            isSharedFacility: facility.isSharedFacility || false,
            // subName is used in UI comparison logic (line 1504)
            subName: facility.facilityName,
            paymentModel: facility.paymentModel || facility.PaymentModel || null
          });
        });

        const regionsData = Object.values(grouped);

        setRegions(regionsData);
        setError(null);
      } else {
        setError(response.resultMessage || 'Veriler yüklenirken bir hata oluştu.');
      }
    } catch (err) {
      console.error('Tesisler yüklenirken hata:', err);
      setError('Veriler yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableTimeSlots = async (facilityId, date, sportTypeId = null) => {
    const requestKey = `${facilityId}|${date}|${sportTypeId ?? 'all'}`;
    latestSlotRequestKeyRef.current = requestKey;
    setIsSlotsLoading(true);

    try {
      const response = await getSportsAvailableSlots(facilityId, date, sportTypeId);

      if (response.isSuccess) {
        if (latestSlotRequestKeyRef.current !== requestKey) {
          return;
        }

        setSelectedFacility(prev => {
          if (!prev || String(prev.id) !== String(facilityId)) {
            return prev;
          }

          return {
            ...prev,
            timeSlots: response.data || []
          };
        });
      }
    } catch (err) {
      console.error('Saat dilimleri yüklenirken hata:', err);
    } finally {
      if (latestSlotRequestKeyRef.current === requestKey) {
        setIsSlotsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!selectedFacility?.id) return undefined;
    if (isClubApplicationMode || isCancellationMode || isCardApplicationMode || isBalanceLoadMode || isPublicEducationMode || isSummerSportsMode || isTennisAcademyMode) return undefined;
    if (viewStep !== 'slots') return undefined;

    const refreshSlots = () => {
      fetchAvailableTimeSlots(selectedFacility.id, selectedDate, selectedSportType?.typeId);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshSlots();
      }
    };

    const handleFocus = () => {
      refreshSlots();
    };

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        refreshSlots();
      }
    }, 30000);

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    selectedFacility?.id,
    selectedDate,
    selectedSportType?.typeId,
    viewStep,
    isClubApplicationMode,
    isCancellationMode,
    isCardApplicationMode,
    isBalanceLoadMode,
    isPublicEducationMode,
    isSummerSportsMode,
    isTennisAcademyMode
  ]);

  const handleCreateAppointment = async () => {
    if (isCreditFacility) {
      if (!formData.idNumber || !formData.phone) {
        toast.warn('Kontörlü randevu için TC kimlik numarası ve telefon zorunludur.');
        return;
      }
      if (formData.idNumber.replace(/\D/g, '').length !== 11) {
        toast.warn('TC Kimlik numarası 11 haneli olmalıdır.');
        return;
      }
      if (formData.phone.replace(/\D/g, '').length !== 10) {
        toast.warn('Telefon numarası 10 haneli olmalıdır. (5XXXXXXXXX)');
        return;
      }
      await handleFitnessReservation();
      return;
    }
    if (!formData.firstName || !formData.lastName || !formData.idNumber || !formData.email || !formData.phone || !formData.userGender) {
      toast.warn('Lütfen tüm zorunlu alanları doldurunuz.');
      return;
    }

    // TC Kimlik: 11 hane zorunlu
    if (formData.idNumber.replace(/\D/g, '').length !== 11) {
      toast.warn('TC Kimlik numarası 11 haneli olmalıdır.');
      return;
    }

    // Telefon: 10 hane zorunlu
    if (formData.phone.replace(/\D/g, '').length !== 10) {
      toast.warn('Telefon numarası 10 haneli olmalıdır. (5XXXXXXXXX)');
      return;
    }

    // Navigate to payment step inline
    setViewStep('payment');
  };

  const getAppointmentProgramLabel = () => {
    if (isFitnessFacility || creditProgramType === 'Fitness') return 'Fitness';
    if (isPoolFacility || creditProgramType === 'Pool') return 'Havuz';
    return selectedSportType?.typeName || selectedFacility?.type || selectedFacility?.name || 'seçilen spor';
  };

  const handleProceedFromConfirmation = () => {
    if (isFitnessFacility || isPoolFacility) {
      setShowProgramConfirmModal(true);
      return;
    }

    setViewStep('payment');
  };

  const confirmProceedFromConfirmation = () => {
    setShowProgramConfirmModal(false);

    if (isCreditFacility) {
      handleFitnessReservation();
      return;
    }

    setViewStep('payment');
  };

  const handleFitnessReservation = async () => {
    if (!selectedFacility || !selectedTimeSlot) {
      return;
    }

    if (!formData.idNumber || !formData.phone) {
      toast.warn('Kontörlü randevu için TC kimlik numarası ve telefon zorunludur.');
      return;
    }

    if (formData.idNumber.replace(/\D/g, '').length !== 11) {
      toast.warn('TC Kimlik numaras? 11 haneli olmal?d?r.');
      return;
    }

    if (formData.phone.replace(/\D/g, '').length !== 10) {
      toast.warn('Telefon numaras? 10 haneli olmal?d?r. (5XXXXXXXXX)');
      return;
    }

    setAppointmentDetails(null);
    setAppointmentDetailsLoading(true);
    setFitnessReservationResult(null);

    try {
      const response = await createSportsAppointment({
        facilityId: selectedFacility.id,
        appointmentDate: selectedDate,
        startTime: selectedTimeSlot.time.split(' - ')[0],
        endTime: selectedTimeSlot.time.split(' - ')[1],
        firstName: formData.firstName || '',
        lastName: formData.lastName || '',
        identityNumber: formData.idNumber,
        email: formData.email || '',
        phone: formData.phone,
        createdByWeb: true,
        isPaid: true,
        reservationType: 'Individual',
        personCount: 1,
        userGender: formData.userGender || null,
        programType: creditProgramType
      });

      if (!response?.isSuccess) {
        toast.error(response?.resultMessage || 'Kontörlü randevu oluşturulamadı.');
        return;
      }

      setFitnessReservationResult({
        message: response.data?.message || 'Kontörlü randevu oluşturuldu.',
        remainingCredits: response.data?.remainingCredits ?? null,
        appointmentId: response.data?.appointmentId ?? null
      });
      setPaymentOutcome('success');
      setPaymentError(null);
      setPaymentResultData(null);
      setViewStep('payment');

      if (response.data?.appointmentId) {
        const appointmentResult = await getSportsAppointmentById(response.data.appointmentId);
        if (appointmentResult?.isSuccess) {
          setAppointmentDetails(appointmentResult.data);
        }
      }
    } catch (err) {
      console.error('Fitness randevu olusturma hatasi:', err);
      toast.error('Fitness randevusu olusturulurken bir hata olustu.');
    } finally {
      setAppointmentDetailsLoading(false);
    }
  };

  const detectCardType = (cardNumber) => {
    const cleaned = cardNumber.replace(/\s/g, '');
    // Visa starts with 4
    if (cleaned.startsWith('4')) return '1';
    // MasterCard starts with 51-55 or 2221-2720
    if (/^5[1-5]/.test(cleaned) || /^2[2-7]/.test(cleaned)) return '2';
    // Default to unknown
    return null;
  };

  // Luhn Algorithm for Credit Card Validation
  const luhnCheck = (val) => {
    let checksum = 0;
    let j = 1;
    for (let i = val.length - 1; i >= 0; i--) {
      let calc = 0;
      calc = Number(val.charAt(i)) * j;
      if (calc > 9) {
        checksum = checksum + 1;
        calc = calc - 10;
      }
      checksum = checksum + calc;
      if (j == 1) {
        j = 2;
      } else {
        j = 1;
      }
    }
    return (checksum % 10) == 0;
  };

  const handlePayment = async () => {
    setPaymentOutcome(null);
    setPaymentResultData(null);
    setPaymentError(null);
    setAppointmentDetails(null);
    setFitnessReservationResult(null);
    const errors = {};
    const cardNumber = paymentData.cardNumber.replace(/\s/g, '');
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    // Validate Card Number
    if (!cardNumber) {
      errors.cardNumber = 'Kart numarası gereklidir.';
    } else if (cardNumber.length !== 16) {
      errors.cardNumber = 'Kart numarası 16 haneli olmalıdır.';
    } else if (!luhnCheck(cardNumber)) {
      errors.cardNumber = 'Geçersiz kart numarası.';
    }

    // Validate Card Name
    if (!paymentData.cardName.trim()) {
      errors.cardName = 'Kart üzerindeki isim gereklidir.';
    }

    // Validate Expiry
    if (!paymentData.expiryMonth || !paymentData.expiryYear) {
      errors.expiry = 'Son kullanma tarihi seçiniz.';
    } else {
      const expYear = parseInt(paymentData.expiryYear);
      const expMonth = parseInt(paymentData.expiryMonth);
      if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
        errors.expiry = 'Kartın son kullanma tarihi geçmiştir.';
      }
    }

    // Validate CVV
    if (!paymentData.cvv) {
      errors.cvv = 'CVV gereklidir.';
    } else if (paymentData.cvv.length !== 3) {
      errors.cvv = 'CVV 3 haneli olmalıdır.';
    }

    if (Object.keys(errors).length > 0) {
      setPaymentErrors(errors);
      return; // Stop if validation fails
    }

    setPaymentErrors({}); // Clear errors if valid

    try {
      // Prepare payment initiation data
      const paymentRequestData = {
        facilityId: selectedFacility.id,
        appointmentDate: selectedDate,
        startTime: selectedTimeSlot.time.split(' - ')[0],
        endTime: selectedTimeSlot.time.split(' - ')[1],
        firstName: formData.firstName,
        lastName: formData.lastName,
        identityNumber: formData.idNumber,
        email: formData.email,
        phone: formData.phone,
        pan: paymentData.cardNumber.replace(/\s/g, ''),
        cv2: paymentData.cvv,
        expireMonth: paymentData.expiryMonth.padStart(2, '0'),
        expireYear: paymentData.expiryYear,
        cardType: detectCardType(paymentData.cardNumber),
        amount: reservationType === 'Club'
          ? (selectedFacility.clubPricePerPerson * (personCount || 1))
          : selectedTimeSlot.price,
        sportTypeId: selectedSportType ? selectedSportType.typeId : null,
        userGender: formData.userGender,
        reservationType: reservationType,
        personCount: personCount || 1
      };

      const response = await initiatePayment(paymentRequestData);

      if (response.isSuccess && response.data?.html) {
        // Set embedded HTML content
        setPaymentHtml(response.data.html);
        setViewStep('payment');
        if (response.data.transactionId) {
          setPendingTransactionId(response.data.transactionId);
        }
      } else {
        alert(response.resultMessage || 'Ödeme başlatılamadı. Lütfen tekrar deneyin.');
      }
    } catch (err) {
      console.error('Ödeme başlatılırken hata:', err);
      alert('Ödeme işlemi başlatılırken bir hata oluştu. Lütfen tekrar deneyin.');
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setVerificationError('Lütfen 6 haneli doğrulama kodunu giriniz.');
      return;
    }

    setIsVerifying(true);
    setVerificationError('');

    try {
      const response = await verifySportsAppointment(pendingAppointmentId, verificationCode);

      if (response.isSuccess) {
        setShowVerificationModal(false);
        setSuccessMessage({
          firstName: formData.firstName,
          lastName: formData.lastName,
          facilityName: selectedFacility.name,
          date: new Date(selectedDate).toLocaleDateString('tr-TR'),
          time: selectedTimeSlot.time
        });
        setShowSuccessModal(true);

        setFormData(initialAppointmentFormData);
        setSelectedTimeSlot(null);
        setVerificationCode('');
        setPendingAppointmentId(null);
        fetchAvailableTimeSlots(selectedFacility.id, selectedDate, selectedSportType?.typeId);
      } else {
        setVerificationError(response.resultMessage || 'Doğrulama kodu hatalı.');
      }
    } catch (err) {
      console.error('Doğrulama hatası:', err);
      setVerificationError('Doğrulama kodu hatalı veya süresi dolmuş.');
    } finally {
      setIsVerifying(false);
    }
  };

  // Ref for the regions list container
  const regionsListRef = useRef(null);

  const toggleRegion = (regionId) => {
    setExpandedRegions(prev => {
      const isExpanded = prev.includes(regionId);
      if (isExpanded) {
        return prev.filter(id => id !== regionId);
      } else {
        // Scroll to region after a short delay to allow rendering
        setTimeout(() => {
          const element = document.getElementById(`region-${regionId}`);
          const container = regionsListRef.current;

          if (element && container) {
            // Calculate position relative to the container
            const containerRect = container.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();

            // Current scroll position of the container
            const currentScrollTop = container.scrollTop;

            // Calculate new scroll position: current scroll + element's offset from container top - padding (optional)
            // relativeTop is the distance from the top of the container's *viewport*
            const relativeTop = elementRect.top - containerRect.top;

            // Try to align element at the top of the container
            container.scrollTo({
              top: currentScrollTop + relativeTop - 10, // 10px buffer
              behavior: 'smooth'
            });
          }
        }, 100);
        return [...prev, regionId];
      }
    });
  };

  const handleFacilityClick = async (facility, region) => {
    const isSameSelection =
      selectedFacility?.id === facility.id &&
      (selectedFacility?.subName || '') === (facility.subName || '');

    if (isSameSelection) return;

    setIsClubApplicationMode(false);
    setIsCancellationMode(false);
    setIsCardApplicationMode(false);
    setIsBalanceLoadMode(false);
    setIsPublicEducationMode(false);
    setIsSummerSportsMode(false);
    setIsTennisAcademyMode(false);
    setIsSlotsLoading(true);
    setSelectedFacility({
      ...facility,
      regionName: region.name,
      timeSlots: []
    });
    setSelectedTimeSlot(null);
    setReservationType('Individual');
    setPersonCount(1);

    // Reset view step to slots when switching facilities
    setViewStep('slots');

    // Reset form data and policy states
    setFormData(initialAppointmentFormData);
    setTermsAccepted(false);
    setTermsViewed(false);
    setRefundPolicyAccepted(false);
    setRefundPolicyViewed(false);
    setContractAccepted(false);
    setContractViewed(false);

    // Reset payment states
    setPaymentData({ cardNumber: '', cardName: '', expiryMonth: '', expiryYear: '', cvv: '' });
    setPaymentErrors({});
    setPaymentHtml(null);
    setPaymentOutcome(null);
    setPaymentError(null);
    setFitnessReservationResult(null);

    // Handle multi-sport facility selection
    if (facility.isSharedFacility && facility.availableSports?.length > 0) {
      // Default to first sport
      setSelectedSportType(facility.availableSports[0]);
    } else {
      setSelectedSportType(null);
    }

    // Fetch gender restrictions
    try {
      const res = await getGenderRestrictions(facility.id);
      if (res.isSuccess) {
        setGenderRestrictions(res.data);
      } else {
        setGenderRestrictions([]);
      }
    } catch (err) {
      console.error('Kısıtlamalar alınamadı:', err);
      setGenderRestrictions([]);
    }

    // Immediately fetch available time slots for the selected facility
    const sportTypeId = (facility.isSharedFacility && facility.availableSports?.length > 0)
      ? facility.availableSports[0].typeId
      : null;
    fetchAvailableTimeSlots(facility.id, selectedDate, sportTypeId);
  };

  // Handle Club Application Submit
  const handleClubApplication = async () => {
    // Validate required fields
    if (!clubApplicationData.firstName || !clubApplicationData.lastName ||
      !clubApplicationData.idNumber || !clubApplicationData.taxNumber ||
      !clubApplicationData.email || !clubApplicationData.phone ||
      !clubApplicationData.regionId) {
      setClubApplicationError('Lütfen tüm zorunlu alanları doldurunuz.');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(clubApplicationData.email)) {
      setClubApplicationError('Lütfen geçerli bir e-posta adresi giriniz.');
      return;
    }

    // Validate TC number (11 digits)
    if (clubApplicationData.idNumber.length !== 11) {
      setClubApplicationError('TC Kimlik numarası 11 haneli olmalıdır.');
      return;
    }

    // Validate phone (10 digits)
    if (clubApplicationData.phone.length !== 10) {
      setClubApplicationError('Telefon numarası 10 haneli olmalıdır.');
      return;
    }

    // Validate tax number (10 digits)
    if (clubApplicationData.taxNumber.replace(/\D/g, '').length !== 10) {
      setClubApplicationError('Vergi numarası 10 haneli olmalıdır.');
      return;
    }

    setClubApplicationLoading(true);
    setClubApplicationError('');

    try {
      const selectedRegion = regions.find(r => String(r.id) === String(clubApplicationData.regionId));

      const payload = {
        firstName: clubApplicationData.firstName,
        lastName: clubApplicationData.lastName,
        identityNumber: clubApplicationData.idNumber,
        taxNumber: clubApplicationData.taxNumber,
        email: clubApplicationData.email,
        phone: clubApplicationData.phone,
        facilityId: parseInt(clubApplicationData.regionId) || 0,
        facilityName: selectedRegion ? selectedRegion.name : '',
        sportType: clubApplicationData.sportType || null,
        responsibleDays: 'Tahsis Paneli Üzerinden Belirlenecek'
      };

      const response = await createClubApplication(payload);

      if (response.isSuccess) {
        setClubApplicationSuccess(true);
        // Reset form after 3 seconds
        setTimeout(() => {
          setIsClubApplicationMode(false);
          setClubApplicationSuccess(false);
          setClubSchedule({});
          setClubApplicationData({
            firstName: '',
            lastName: '',
            idNumber: '',
            taxNumber: '',
            email: '',
            phone: '',
            regionId: '',
            sportType: '',
            responsibleDays: []
          });
        }, 3000);
      } else {
        setClubApplicationError(response.resultMessage || 'Başvuru gönderilemedi. Lütfen tekrar deneyin.');
      }
    } catch (err) {
      console.error('Kulüp başvurusu hatası:', err);
      setClubApplicationError('Başvuru sırasında bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setClubApplicationLoading(false);
    }
  };

  const handleVerifyClub = async () => {
    if (!clubVerifyData.taxNumber || !clubVerifyData.phone) {
      toast.warn('Lütfen Vergi No ve Telefon bilgilerini giriniz.');
      return;
    }

    setVerifyingClub(true);
    try {
      const result = await verifyClubRegistration({
        taxNumber: clubVerifyData.taxNumber,
        phone: clubVerifyData.phone
      });

      if (result.isSuccess) {
        toast.success(result.resultMessage || 'Kulüp bilgileri doğrulandı. İşleme devam edebilirsiniz.');
        setIsClubVerified(true);

        // Auto-fill form data with verified club details
        const club = result.data;
        // Masking helper
        const maskName = (name) => {
          if (!name || name.length < 2) return name;
          return name[0] + '*'.repeat(name.length - 1);
        };

        setFormData(prev => ({
          ...prev,
          firstName: maskName(club.firstName),
          lastName: maskName(club.lastName),
          idNumber: club.identityNumber,
          email: club.email,
          phone: club.phone
        }));
      } else {
        toast.error(result.resultMessage || 'Kulüp bilgileri doğrulanamadı.');
      }
    } catch (err) {
      console.error('Club verification error:', err);
      toast.error('Doğrulama sırasında bir hata oluştu.');
    } finally {
      setVerifyingClub(false);
    }
  };

  // Toggle day selection for club application
  // Toggle day selection for club application
  const toggleResponsibleDay = (day) => {
    const isSelected = clubApplicationData.responsibleDays.includes(day);

    setClubApplicationData(prev => ({
      ...prev,
      responsibleDays: isSelected
        ? prev.responsibleDays.filter(d => d !== day)
        : [...prev.responsibleDays, day]
    }));

    // If selecting a new day, initialize schedule for it if empty
    if (!isSelected && (!clubSchedule[day] || clubSchedule[day].length === 0)) {
      setClubSchedule(prev => ({
        ...prev,
        [day]: [{ start: '09:00', end: '10:00' }]
      }));
    }
  };

  const addTimeSlot = (day) => {
    setClubSchedule(prev => {
      const currentSlots = prev[day] || [];
      let newStart = '09:00';
      let newEnd = '10:00';

      if (currentSlots.length > 0) {
        const lastSlot = currentSlots[currentSlots.length - 1];
        if (lastSlot.end) {
          const [hours, minutes] = lastSlot.end.split(':').map(Number);
          const nextHour = (hours + 1) % 24;
          const nextNextHour = (hours + 2) % 24;

          newStart = `${String(nextHour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
          newEnd = `${String(nextNextHour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        }
      }

      return {
        ...prev,
        [day]: [...currentSlots, { start: newStart, end: newEnd }]
      };
    });
  };

  const removeTimeSlot = (day, index) => {
    setClubSchedule(prev => {
      const newSlots = [...(prev[day] || [])];
      newSlots.splice(index, 1);
      return { ...prev, [day]: newSlots };
    });
  };

  const updateTimeSlot = (day, index, field, value) => {
    setClubSchedule(prev => {
      const newSlots = [...(prev[day] || [])];
      const newSlot = { ...newSlots[index], [field]: value };
      newSlots[index] = newSlot;
      return { ...prev, [day]: newSlots };
    });
  };

  // Join Cancellation Handlers
  const calculateHoursUntil = (appointmentDate, startTime) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const apptDateTime = new Date(appointmentDate);
    apptDateTime.setHours(hours, minutes, 0, 0);
    const now = new Date();
    const diffMs = apptDateTime - now;
    return Math.floor(diffMs / (1000 * 60 * 60));
  };

  const handleCancellationSearch = async (e) => {
    e.preventDefault();

    if (!cancellationIdentity || cancellationIdentity.length !== 11) {
      toast.error('Lütfen geçerli bir TC Kimlik Numarası giriniz.');
      return;
    }

    setCancellationLoading(true);
    setCancellationSearched(false);
    const result = await getSportsAppointmentsByIdentity(cancellationIdentity);
    setCancellationLoading(false);

    if (result.isSuccess) {
      const activeAppointments = result.data.filter(apt =>
        apt.status === 'Confirmed' || apt.status === 'Pending'
      );
      setCancellationAppointments(activeAppointments);
      setCancellationSearched(true);
      if (activeAppointments.length === 0) {
        toast.info('Adınıza kayıtlı aktif randevu bulunamadı.');
      }
    } else {
      toast.error(result.resultMessage || 'Bir hata oluştu.');
    }
  };

  const handleRequestCancel = async (appointment) => {
    setCancellationSelectedAppointment(appointment);
    setCancellationSubmitting(true);

    const result = await requestSportsCancellation({
      appointmentId: appointment.id,
      identityNumber: cancellationIdentity
    });
    setCancellationSubmitting(false);

    if (result.isSuccess) {
      toast.success('Doğrulama kodu e-posta adresinize gönderildi.');
      setCancelVerificationCode('');
      setShowOtpModal(true);
    } else {
      toast.error(result.resultMessage || 'İptal talebi gönderilemedi.');
    }
  };

  const handleConfirmCancel = async () => {
    if (!cancelVerificationCode || cancelVerificationCode.length < 6) {
      toast.warn('Lütfen 6 haneli doğrulama kodunu giriniz.');
      return;
    }

    setCancellationSubmitting(true);

    const result = await confirmSportsCancellation({
      appointmentId: cancellationSelectedAppointment.id,
      verificationCode: cancelVerificationCode
    });
    setCancellationSubmitting(false);

    if (result.isSuccess) {
      const data = result.data;
      if (data.refundProcessed) {
        toast.success(`${data.message} İade işlemi başlatıldı.`);
      } else {
        toast.success(data.message);
      }

      setShowOtpModal(false);
      const newList = cancellationAppointments.filter(x => x.id !== cancellationSelectedAppointment.id);
      setCancellationAppointments(newList);
      setCancellationSelectedAppointment(null);
      setCancelVerificationCode('');
    } else {
      toast.error(result.resultMessage || 'İptal işlemi başarısız.');
    }
  };

  // Get all facilities for club application dropdown
  const getAllFacilities = () => {
    const facilities = [];
    regions.forEach(region => {
      region.facilities.forEach(facility => {
        facilities.push({
          id: facility.id,
          name: `${region.name} - ${facility.name}`,
          type: facility.type
        });
      });
    });
    return facilities;
  };

  if (loading) {
    return (
      <div className="sports-appointment-page">
        <div className="container">
          <header className="sa-header">
            <h1>Spor Tesisleri Randevu Sistemi</h1>
          </header>
          <div className="sa-loading">
            <p>Yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sports-appointment-page">
        <div className="container">
          <header className="sa-header">
            <h1>Spor Tesisleri Randevu Sistemi</h1>
          </header>
          <div className="sa-error">
            <p>{error}</p>
            <button onClick={fetchRegionsAndFacilities} className="sa-retry-button">Tekrar Dene</button>
          </div>
        </div>
      </div>
    );
  }

  if (isInIframe) {
    return null;
  }

  const isFitnessFacility = (selectedFacility?.type || '').toLowerCase().includes('fitness');
  const isPoolFacility = (selectedFacility?.type || '').toLowerCase().includes('havuz') || (selectedFacility?.type || '').toLowerCase().includes('pool');
  const isCreditFacility = (selectedFacility?.paymentModel || '').toLowerCase() === 'credit' || isFitnessFacility;
  const creditProgramType = isFitnessFacility ? 'Fitness' : (isPoolFacility ? 'Pool' : 'Pool');
  const getSlotCapacityLabel = (slot) => {
    if (!slot) return '';
    const blockSource = String(slot.blockSource || '').toLowerCase();
    const isBlockedSlot = blockSource === 'club' || blockSource === 'memberhour' || blockSource === 'ministryhour';
    const capacity = Number(slot.capacity ?? slot.Capacity ?? 0);
    const currentCount = Number(slot.currentCount ?? slot.CurrentCount ?? 0);

    return Number.isFinite(capacity) && capacity > 1 && !isBlockedSlot && slot.available
      ? `Kapasite: ${Math.max(currentCount, 0)} / ${capacity}`
      : '';
  };
  const selectedSlotCapacityLabel = getSlotCapacityLabel(selectedTimeSlot);
  const paymentTotal = isCreditFacility
    ? 0
    : reservationType === 'Club'
    ? (() => {
        if (selectedTimeSlot?.time && selectedFacility?.clubNightStartTime && selectedFacility?.clubNightPricePerPerson) {
          const startTime = selectedTimeSlot.time.split(' ')[0];
          const slotTimeParts = startTime.split(':').map(Number);
          const nightTimeParts = selectedFacility.clubNightStartTime.split(':').map(Number);
          const isNight = slotTimeParts[0] > nightTimeParts[0] || (slotTimeParts[0] === nightTimeParts[0] && slotTimeParts[1] >= nightTimeParts[1]);
          const price = isNight ? selectedFacility.clubNightPricePerPerson : (selectedFacility?.clubPricePerPerson || 0);
          return price * (personCount || 1);
        }
        return (selectedFacility?.clubPricePerPerson || 0) * (personCount || 1);
      })()
    : (selectedTimeSlot?.price || 0);
  const previewCardNumber = paymentData.cardNumber || '0000 0000 0000 0000';
  const previewCardHolder = paymentData.cardName || `${formData.firstName} ${formData.lastName}`.trim() || 'AD SOYAD';
  const previewExpiry = paymentData.expiryMonth && paymentData.expiryYear
    ? `${paymentData.expiryMonth}/${String(paymentData.expiryYear).slice(-2)}`
    : 'AA/YY';
  const turkeyToday = getTurkeyDateString();
  const turkeyTomorrow = addDaysToDateString(turkeyToday, 1);
  // Time gate: public site opens at 02.03.2026 00:00 Turkey time
  const openDate = '2026-03-01';
  if (turkeyToday < openDate) {
    return (
      <div className="sports-appointment-page">
        <div className="container" style={{ textAlign: 'center', padding: '4rem 1rem' }}>
          <div style={{
            maxWidth: '600px',
            margin: '0 auto',
            background: 'linear-gradient(135deg, #f0f7fc, #e1eff8)',
            borderRadius: '24px',
            padding: '3rem 2rem',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            border: '1px solid #b3d4ea'
          }}>
            <FaClock size={48} style={{ color: '#0f75bc', marginBottom: '1.5rem' }} />
            <h2 style={{ color: '#1e3a5f', fontSize: '1.6rem', fontWeight: '800', marginBottom: '1rem' }}>
              Spor Tesisleri Randevu Sistemi
            </h2>
            <p style={{ color: '#475569', fontSize: '1.1rem', lineHeight: '1.7', marginBottom: '1.5rem' }}>
              Randevu sistemimiz <strong style={{ color: '#0f75bc' }}>2 Mart 2026</strong> tarihinde hizmete açılacaktır.
            </p>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '1.25rem',
              border: '1px solid #e2e8f0',
              display: 'inline-block'
            }}>
              <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '600', marginBottom: '0.5rem' }}>Açılış Tarihi</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#0f75bc' }}>02.03.2026 - 00:00</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sports-appointment-page">
      <ToastContainer position="top-center" />
      <div className="container">
        <header className="sa-header">
          <h1>Spor Tesisleri Randevu Sistemi</h1>
          <p>
            İskenderun Belediyesi spor tesislerini online randevu sistemi ile kiralayabilirsiniz.
            Tenis kortları, futbol sahaları, basketbol ve voleybol sahaları saatlik kiralama ile hizmetinizde.
          </p>
        </header>

        <div className="sa-content" style={{
          display: 'grid',
        }}>

          <div className="sa-action-bar" style={{
            gridColumn: '1 / -1',
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '20px'
          }}>
            <div style={{
              background: '#f1f5f9',
              padding: '6px',
              borderRadius: '16px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)',
              maxWidth: '100%'
            }}>
              {/* Tab 1: Randevu Al - GİZLİ (daha sonra aktif edilecek) */}
              {true && (
                <button
                  type="button"
                  onClick={() => {
                    setIsClubApplicationMode(false);
                    setIsCancellationMode(false);
                    setIsCardApplicationMode(false);
                    setIsBalanceLoadMode(false);
                    setIsPublicEducationMode(false);
                    setIsSummerSportsMode(false);
                    setIsTennisAcademyMode(false);
                    setIsClubPaymentMode(false);
                    setIsCardManagementMode(false);
                    setSelectedFacility(null);
                  }}
                  style={{
                    border: 'none',
                    background: (!isClubApplicationMode && !isCancellationMode && !isCardApplicationMode && !isBalanceLoadMode && !isPublicEducationMode && !isSummerSportsMode && !isTennisAcademyMode && !isClubPaymentMode && !isCardManagementMode) ? '#ffffff' : 'transparent',
                    color: (!isClubApplicationMode && !isCancellationMode && !isCardApplicationMode && !isBalanceLoadMode && !isPublicEducationMode && !isSummerSportsMode && !isTennisAcademyMode && !isClubPaymentMode && !isCardManagementMode) ? '#3b82f6' : '#64748b',
                    padding: '12px 24px',
                    borderRadius: '12px',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    boxShadow: (!isClubApplicationMode && !isCancellationMode && !isCardApplicationMode && !isBalanceLoadMode && !isPublicEducationMode && !isSummerSportsMode && !isTennisAcademyMode && !isClubPaymentMode && !isCardManagementMode) ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' : 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'nowrap',
                    flex: '1 1 auto',
                    minWidth: '120px',
                    gap: '8px'
                  }}
                >
                  <FaCalendarDay />
                  <span>Randevu Al</span>
                </button>
              )}

              {/* Tab 2: Kulüp İşlemleri */}
              {(
                <button
                  type="button"
                  onClick={() => {
                    setIsClubApplicationMode(true);
                    setIsCancellationMode(false);
                    setIsCardApplicationMode(false);
                    setIsBalanceLoadMode(false);
                    setIsPublicEducationMode(false);
                    setIsSummerSportsMode(false);
                    setIsTennisAcademyMode(false);
                    setIsClubPaymentMode(false);
                    setIsCardManagementMode(false);
                    setSelectedFacility(null);
                    setClubApplicationError('');
                    setClubApplicationSuccess(false);
                  }}
                  style={{
                    border: 'none',
                    background: isClubApplicationMode ? '#ffffff' : 'transparent',
                    color: isClubApplicationMode ? '#3b82f6' : '#64748b',
                    padding: '12px 24px',
                    borderRadius: '12px',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    boxShadow: isClubApplicationMode ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' : 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'nowrap',
                    flex: '1 1 auto',
                    minWidth: '120px',
                    gap: '8px'
                  }}
                >
                  <FaUsers />
                  <span>Kulüp İşlemleri</span>
                </button>
              )}

              {/* Tab 3: Randevu İptal - GİZLİ (daha sonra aktif edilecek) */}
              {false && (
                <button
                  type="button"
                  onClick={() => {
                    setIsCancellationMode(true);
                    setIsClubApplicationMode(false);
                    setIsCardApplicationMode(false);
                    setIsBalanceLoadMode(false);
                    setIsPublicEducationMode(false);
                    setIsSummerSportsMode(false);
                    setIsTennisAcademyMode(false);
                    setIsClubPaymentMode(false);
                    setIsCardManagementMode(false);
                    setSelectedFacility(null);
                    setClubApplicationError('');
                    setClubApplicationSuccess(false);
                  }}
                  style={{
                    border: 'none',
                    background: isCancellationMode ? '#ffffff' : 'transparent',
                    color: isCancellationMode ? '#dc2626' : '#64748b',
                    padding: '12px 24px',
                    borderRadius: '12px',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    boxShadow: isCancellationMode ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' : 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'nowrap',
                    flex: '1 1 auto',
                    minWidth: '120px',
                    gap: '8px'
                  }}
                >
                  <FaTimesCircle />
                  <span>Randevu İptal/Sorgula</span>
                </button>
              )}

              {/* Tab 4: Kart İşlemleri */}
              {true && (
                <button
                  type="button"
                  onClick={() => {
                    setIsCardManagementMode(true);
                    setIsCardApplicationMode(false);
                    setIsBalanceLoadMode(false);
                    setIsClubApplicationMode(false);
                    setIsCancellationMode(false);
                    setIsPublicEducationMode(false);
                    setIsSummerSportsMode(false);
                    setIsTennisAcademyMode(false);
                    setIsClubPaymentMode(false);
                    setSelectedFacility(null);
                  }}
                  style={{
                    border: 'none',
                    background: isCardManagementMode ? '#ffffff' : 'transparent',
                    color: isCardManagementMode ? '#e02d1b' : '#64748b',
                    padding: '12px 24px',
                    borderRadius: '12px',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    boxShadow: isCardManagementMode ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' : 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'nowrap',
                    flex: '1 1 auto',
                    minWidth: '120px',
                    gap: '8px'
                  }}
                >
                  <FaIdCard />
                  <span>Üyelik İşlemleri</span>
                </button>
              )}

              {/* Tab 5: Halk Eğitim Başvurusu - GİZLİ (daha sonra aktif edilecek) */}
              {false && (
                <button
                  type="button"
                  onClick={() => {
                    setIsPublicEducationMode(true);
                    setIsSummerSportsMode(false);
                    setIsClubApplicationMode(false);
                    setIsCancellationMode(false);
                    setIsCardApplicationMode(false);
                    setIsBalanceLoadMode(false);
                    setIsTennisAcademyMode(false);
                    setSelectedFacility(null); // Reset facility selection to show only form
                  }}
                  style={{
                    border: 'none',
                    background: isPublicEducationMode ? '#ffffff' : 'transparent',
                    color: isPublicEducationMode ? '#059669' : '#64748b',
                    padding: '12px 24px',
                    borderRadius: '12px',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    boxShadow: isPublicEducationMode ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' : 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'nowrap',
                    flex: '1 1 auto',
                    minWidth: '120px',
                    gap: '8px'
                  }}
                >
                  <FaGraduationCap />
                  <span>Halk Eğitim</span>
                </button>
              )}


              {/* Tab 5: Bakiye Yükle */}
              <button
                type="button"
                onClick={() => {
                  setIsSummerSportsMode(true);
                  setIsPublicEducationMode(false);
                  setIsClubApplicationMode(false);
                  setIsCancellationMode(false);
                  setIsCardApplicationMode(false);
                  setIsBalanceLoadMode(false);
                  setIsTennisAcademyMode(false);
                  setIsClubPaymentMode(false);
                  setIsCardManagementMode(false);
                  setSelectedFacility(null);
                }}
                style={{
                  border: 'none',
                  background: isSummerSportsMode ? '#ffffff' : 'transparent',
                  color: isSummerSportsMode ? '#c2410c' : '#64748b',
                  padding: '12px 24px',
                  borderRadius: '12px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  boxShadow: isSummerSportsMode ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' : 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  whiteSpace: 'nowrap',
                  flex: '1 1 auto',
                  minWidth: '120px',
                  gap: '8px'
                }}
              >
                <FaSun />
                <span>Yaz Kursları</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsTennisAcademyMode(true);
                  setIsPublicEducationMode(false);
                  setIsSummerSportsMode(false);
                  setIsClubApplicationMode(false);
                  setIsCancellationMode(false);
                  setIsCardApplicationMode(false);
                  setIsBalanceLoadMode(false);
                  setIsClubPaymentMode(false);
                  setIsCardManagementMode(false);
                  setSelectedFacility(null);
                }}
                style={{
                  border: 'none',
                  background: isTennisAcademyMode ? '#ffffff' : 'transparent',
                  color: isTennisAcademyMode ? '#7c3aed' : '#64748b',
                  padding: '12px 24px',
                  borderRadius: '12px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  boxShadow: isTennisAcademyMode ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' : 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  whiteSpace: 'nowrap',
                  flex: '1 1 auto',
                  minWidth: '120px',
                  gap: '8px'
                }}
              >
                <GiTennisRacket />
                <span>Tenis Akademi</span>
              </button>





            </div>
          </div>
          {!isClubApplicationMode && !isCancellationMode && !isCardApplicationMode && !isBalanceLoadMode && !isPublicEducationMode && !isSummerSportsMode && !isTennisAcademyMode && !isClubPaymentMode && !isCardManagementMode && (
            <div className="sa-left-panel">
              <div className="sa-regions-list" ref={regionsListRef}>
                {regions.map(region => (
                  <div key={region.id} id={`region-${region.id}`} className="sa-region-item">
                    <button
                      type="button"
                      className={`sa-region-header ${expandedRegions.includes(region.id) ? 'expanded' : ''}`}
                      onClick={() => toggleRegion(region.id)}
                    >
                      <span className="sa-region-icon">
                        {expandedRegions.includes(region.id) ? <FaChevronDown /> : <FaChevronRight />}
                      </span>
                      <span className="sa-region-name">{region.name}</span>

                    </button>

                    {expandedRegions.includes(region.id) && (
                      <div className="sa-facilities-list">
                        {region.facilities.map((facility, index) => {
                          const Icon = facilityIcons[facility.type] || FaFutbol;
                          // Check both ID and subName to distinguish between sub-items of the same facility
                          const isSelected = selectedFacility?.id === facility.id &&
                            (facility.subName ? selectedFacility?.subName === facility.subName : true);

                          return (
                            <button
                              key={`${facility.id}-${facility.subName || index}`}
                              type="button"
                              className={`sa-facility-card ${isSelected ? 'selected' : ''}`}
                              onClick={() => {
                                setIsPublicEducationMode(false);
                                setIsSummerSportsMode(false);
                                setIsTennisAcademyMode(false);
                                setIsCardManagementMode(false);
                                handleFacilityClick(facility, region);
                              }}
                            >
                              <div className="sa-facility-icon">
                                <Icon />
                              </div>
                              <div className="sa-facility-info">
                                <div className="sa-facility-name">{facility.name}</div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>

            </div>
          )}

          <div className={`sa-right-panel ${(isClubApplicationMode || isCancellationMode || isCardApplicationMode || isBalanceLoadMode || isPublicEducationMode || isSummerSportsMode || isTennisAcademyMode || isClubPaymentMode || isCardManagementMode) ? 'sa-right-panel--centered sa-right-panel--mode' : ''}`} style={{
            gridColumn: (isClubApplicationMode || isCancellationMode || isCardApplicationMode || isBalanceLoadMode || isPublicEducationMode || isSummerSportsMode || isTennisAcademyMode || isClubPaymentMode || isCardManagementMode) ? '1 / -1' : 'auto',
            background: (isClubApplicationMode || isCancellationMode || isCardApplicationMode || isBalanceLoadMode || isPublicEducationMode || isSummerSportsMode || isTennisAcademyMode || isClubPaymentMode || isCardManagementMode) ? '#fff' : '#fff',
            boxShadow: (isClubApplicationMode || isCancellationMode || isCardApplicationMode || isBalanceLoadMode || isPublicEducationMode || isSummerSportsMode || isTennisAcademyMode || isClubPaymentMode || isCardManagementMode) ? '0 10px 40px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.03)' : '0 4px 20px rgba(0, 0, 0, 0.08)',
            borderRadius: '24px',
            maxWidth: (isClubApplicationMode || isCancellationMode || isCardApplicationMode || isBalanceLoadMode || isPublicEducationMode || isSummerSportsMode || isTennisAcademyMode || isClubPaymentMode || isCardManagementMode) ? '1000px' : 'none',
            width: '100%',
            margin: (isClubApplicationMode || isCancellationMode || isCardApplicationMode || isBalanceLoadMode || isPublicEducationMode || isSummerSportsMode || isTennisAcademyMode || isClubPaymentMode || isCardManagementMode) ? '0 auto' : '0',
            overflow: 'hidden',
            border: (isClubApplicationMode || isCancellationMode || isCardApplicationMode || isBalanceLoadMode || isPublicEducationMode || isSummerSportsMode || isTennisAcademyMode || isClubPaymentMode || isCardManagementMode) ? 'none' : '1px solid #e2e8f0',
            height: 'auto',
            alignSelf: 'start'
          }}>
            {isCancellationMode ? (
              <div className="sa-panel-inner sa-unified-mode sa-cancellation-unified">
                <div className="sports-cancellation-page">
                  <div className="sa-facility-details-header sa-unified-tab-header">
                    <div className="sa-unified-tab-header-row">
                      <h3 className="sa-unified-tab-title">
                        <div className="sa-unified-tab-icon">
                          <FaTimesCircle size={20} />
                        </div>
                        {'Randevu İptal / Sorgula'}
                      </h3>
                    </div>
                    <p className="sa-unified-tab-subtitle">
                      {'Aktif randevularınızı sorgulayıp doğrulama kodu ile iptal işlemi yapabilirsiniz.'}
                    </p>
                  </div>


                  <div className="sc-content-card">

                    <div className="sc-refund-policy">
                      <FaInfoCircle className="sc-policy-icon" style={{ display: 'none' }} />
                      <div>
                        <strong>İade Politikası:</strong>
                        <ul>
                          <li>Randevu saatinden <strong>24 saat veya daha önce</strong> iptal edilen randevularda ücret iadesi yapılır.</li>
                          <li>24 saatten az süre kala yapılan iptallerde randevu iptal edilir ancak ücret iadesi <strong>yapılmaz</strong>.</li>
                        </ul>
                      </div>
                    </div>

                    <form onSubmit={handleCancellationSearch} className="sc-search-form">
                      <div className="sc-form-group">
                        <label htmlFor="cancellationIdentity">TC Kimlik Numarası</label>
                        <input
                          id="cancellationIdentity"
                          type="text"
                          value={cancellationIdentity}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            if (val.length <= 11) setCancellationIdentity(val);
                          }}
                          placeholder="TC Kimlik Numaranız"
                          maxLength={11}
                          required
                        />
                      </div>
                      <button type="submit" className="sc-btn sc-btn-primary" disabled={cancellationLoading}>
                        {cancellationLoading ? 'Sorgulanıyor...' : 'Randevularımı Sorgula'}
                      </button>
                    </form>

                    {cancellationSearched && cancellationAppointments.length === 0 && (
                      <div className="sc-alert sc-alert-info">
                        Bu kimlik numarasına ait aktif randevu bulunamadı.
                      </div>
                    )}

                    <div className="sc-appointments-list">
                      {cancellationAppointments.map((apt) => {
                        return (
                          <div key={apt.id} className="sc-appointment-card">
                            <div className="sc-card-header">
                              <h3>{apt.facilityName}</h3>
                            </div>
                            <div className="sc-card-body">
                              <p><strong>Tarih:</strong> {new Date(apt.appointmentDate).toLocaleDateString('tr-TR', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}</p>
                              <p><strong>Saat:</strong> {apt.startTime} - {apt.endTime}</p>
                              <p><strong>Tutar:</strong> <span className="sc-price">{formatCurrency(apt.paymentAmount)}</span></p>
                              <p className="sc-email"><FaEnvelope /> {apt.email}</p>
                            </div>
                            <div className="sc-card-footer">
                              <button
                                className="sc-btn sc-btn-danger"
                                onClick={() => handleRequestCancel(apt)}
                                disabled={cancellationSubmitting}
                              >
                                <FaTimesCircle /> İptal Et
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                  </div>

                  {/* OTP Modal */}
                  {showOtpModal && (
                    <div className="sc-modal-overlay" onClick={() => setShowOtpModal(false)}>
                      <div className="sc-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="sc-modal-header">
                          <h2>Doğrulama Kodu</h2>
                          <button className="sc-modal-close" onClick={() => setShowOtpModal(false)}>&times;</button>
                        </div>
                        <div className="sc-modal-body">
                          <p>
                            <strong>{cancellationSelectedAppointment?.email}</strong> adresine gönderilen 6 haneli kodu giriniz.
                          </p>
                          <input
                            type="text"
                            className="sc-otp-input"
                            value={cancelVerificationCode}
                            onChange={(e) => setCancelVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="000000"
                            maxLength={6}
                            autoFocus
                          />
                        </div>
                        <div className="sc-modal-footer">
                          <button className="sc-btn sc-btn-secondary" onClick={() => setShowOtpModal(false)}>
                            Kapat
                          </button>
                          <button
                            className="sc-btn sc-btn-danger"
                            onClick={handleConfirmCancel}
                            disabled={cancellationSubmitting}
                          >
                            {cancellationSubmitting ? 'İşleniyor...' : 'Onayla ve İptal Et'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : isCardManagementMode ? (
              <div className="sa-panel-inner sa-panel-inner--wide sa-unified-mode">
                <div style={{ marginBottom: '20px', padding: '15px', background: '#e3f2fd', borderLeft: '4px solid #3b82f6', borderRadius: '8px', display: 'flex', alignItems: 'center' }}>
                  <FaInfoCircle style={{ marginRight: '10px', color: '#3b82f6', fontSize: '1.2rem' }} />
                  <div>
                    <strong>Bilgi:</strong> Üyelik işlemleri havuz ve fitness kart sistemi için kullanılmaktadır.
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '20px', borderBottom: '1px solid #e3e6f0', paddingBottom: '15px' }}>
                  <button
                    onClick={() => setCardManagementSubView('applications')}
                    style={{
                      flex: '1 1 auto',
                      justifyContent: 'center',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      border: cardManagementSubView === 'applications' ? '2px solid #3b82f6' : '1px solid #cbd5e1',
                      background: cardManagementSubView === 'applications' ? '#eff6ff' : '#fff',
                      color: cardManagementSubView === 'applications' ? '#2563eb' : '#64748b',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <FaIdCard /> Kart Başvuru
                  </button>
                  <button
                    onClick={() => setCardManagementSubView('balance')}
                    style={{
                      flex: '1 1 auto',
                      justifyContent: 'center',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      border: cardManagementSubView === 'balance' ? '2px solid #3b82f6' : '1px solid #cbd5e1',
                      background: cardManagementSubView === 'balance' ? '#eff6ff' : '#fff',
                      color: cardManagementSubView === 'balance' ? '#2563eb' : '#64748b',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s'
                    }}
                  >
                  <FaWallet /> Paket Satın Al
                  </button>
                </div>
                {cardManagementSubView === 'applications' && <CardApplicationForm />}
                {cardManagementSubView === 'balance' && (
                  <div className="sa-balance-unified">
                    <CardBalanceLoad 
                      onSelectCardApplication={() => setCardManagementSubView('applications')}
                    />
                  </div>
                )}
              </div>
            ) : isPublicEducationMode ? (
              <PublicEducationRegistrationForm />
            ) : isSummerSportsMode ? (
              <SummerSportsRegistrationForm />
            ) : isTennisAcademyMode ? (
              <TennisAcademyApplicationForm />
            ) : isClubApplicationMode ? (
              <div className="sa-panel-inner sa-panel-inner--wide sa-unified-mode">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '20px', borderBottom: '1px solid #e3e6f0', paddingBottom: '15px' }}>
                  <button
                    onClick={() => setClubManagementSubView('application')}
                    style={{
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      border: clubManagementSubView === 'application' ? '2px solid #3b82f6' : '1px solid #cbd5e1',
                      background: clubManagementSubView === 'application' ? '#eff6ff' : '#fff',
                      color: clubManagementSubView === 'application' ? '#2563eb' : '#64748b',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <FaUsers /> Kulüp Başvurusu
                  </button>
                  <button
                    onClick={() => setClubManagementSubView('payment')}
                    style={{
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      border: clubManagementSubView === 'payment' ? '2px solid #059669' : '1px solid #cbd5e1',
                      background: clubManagementSubView === 'payment' ? '#ecfdf5' : '#fff',
                      color: clubManagementSubView === 'payment' ? '#059669' : '#64748b',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <FaRegMoneyBillAlt /> Kulüp Ödeme
                  </button>
                </div>
                {clubManagementSubView === 'payment' ? (
                  <ClubPayment />
                ) : (
                  <div className="sa-facility-details" style={{
                    maxWidth: '100%',
                    margin: '0',
                    background: 'transparent',
                    borderRadius: '0',
                    boxShadow: 'none',
                    padding: '0',
                    overflow: 'visible',
                    border: 'none'
                  }}>
                    <div className="sa-facility-details-header sa-unified-tab-header">
                      <div className="sa-unified-tab-header-row">
                        <h3 className="sa-unified-tab-title">
                          <div className="sa-unified-tab-icon">
                            <FaUsers size={20} />
                          </div>
                          Kulüp Başvurusu
                        </h3>
                      </div>
                      <p className="sa-unified-tab-subtitle">
                        Kulüp aktiviteleri için tesis kullanım başvurusu oluşturun ve antrenman saatlerinizi detaylı olarak planlayın.
                      </p>
                    </div>

                    {clubApplicationSuccess ? (
                      <div className="sa-club-success" style={{ padding: '60px 40px', textAlign: 'center' }}>
                        <div style={{ width: '80px', height: '80px', background: '#ecfdf5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                          <FaCheckCircle className="sa-success-icon" style={{ fontSize: '40px', color: '#10b981', margin: 0 }} />
                        </div>
                        <h4 style={{ fontSize: '1.5rem', color: '#065f46', marginBottom: '12px' }}>Başvurunuz Alındı!</h4>
                        <p style={{ color: '#4b5563', fontSize: '1.1rem', maxWidth: '500px', margin: '0 auto 32px' }}>Kulüp başvurunuz başarıyla oluşturulmuştur. Yönetici onayından sonra size e-posta yoluyla bilgilendirme yapılacaktır.</p>
                        <button
                          className="sa-btn-verify"
                          onClick={() => {
                            setIsClubApplicationMode(false);
                            setClubApplicationSuccess(false);
                            setClubSchedule({});
                            setClubApplicationData({
                              firstName: '',
                              lastName: '',
                              idNumber: '',
                              taxNumber: '',
                              email: '',
                              phone: '',
                              regionId: '',
                              sportType: '',
                              responsibleDays: []
                            });
                          }}
                          style={{ padding: '12px 32px', fontSize: '1rem', borderRadius: '12px', background: '#10b981', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)' }}
                        >
                          Tamam, Anlaşıldı
                        </button>
                      </div>
                    ) : (
                      <div className="sa-club-form-container">
                        <div className="sa-club-form">
                          {/* BÖLÜM 1: TEMEL BİLGİLER */}
                          <div style={{ marginBottom: '40px' }}>
                            <h4 style={{ fontSize: '0.85rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <span style={{ width: '24px', height: '3px', background: '#e2e8f0', borderRadius: '2px', display: 'block' }}></span>
                              Başvuru Sahibi Bilgileri
                            </h4>
                            <div className="sa-form-grid">
                              <div className="sa-form-group">
                                <label style={{ fontWeight: '600', color: '#334155', marginBottom: '8px', display: 'block' }}>Ad <span className="sa-required" style={{ color: '#ef4444' }}>*</span></label>
                                <input
                                  type="text"
                                  value={clubApplicationData.firstName}
                                  onChange={(e) => setClubApplicationData({ ...clubApplicationData, firstName: e.target.value })}
                                  placeholder="Adınız"
                                  className="sa-form-input"
                                />
                              </div>

                              <div className="sa-form-group">
                                <label style={{ fontWeight: '600', color: '#334155', marginBottom: '8px', display: 'block' }}>Soyad <span className="sa-required" style={{ color: '#ef4444' }}>*</span></label>
                                <input
                                  type="text"
                                  value={clubApplicationData.lastName}
                                  onChange={(e) => setClubApplicationData({ ...clubApplicationData, lastName: e.target.value })}
                                  placeholder="Soyadınız"
                                  className="sa-form-input"
                                />
                              </div>

                              <div className="sa-form-group">
                                <label style={{ fontWeight: '600', color: '#334155', marginBottom: '8px', display: 'block' }}>T.C. Kimlik No <span className="sa-required" style={{ color: '#ef4444' }}>*</span></label>
                                <input
                                  type="text"
                                  value={clubApplicationData.idNumber}
                                  onChange={(e) => setClubApplicationData({ ...clubApplicationData, idNumber: e.target.value.replace(/\D/g, '').slice(0, 11) })}
                                  placeholder="TC Kimlik No"
                                  className="sa-form-input"
                                  maxLength="11"
                                  style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }}
                                />
                              </div>

                              <div className="sa-form-group">
                                <label style={{ fontWeight: '600', color: '#334155', marginBottom: '8px', display: 'block' }}>Vergi Numarası <span className="sa-required" style={{ color: '#ef4444' }}>*</span></label>
                                <input
                                  type="text"
                                  value={clubApplicationData.taxNumber}
                                  onChange={(e) => setClubApplicationData({ ...clubApplicationData, taxNumber: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                                  placeholder="Vergi Numarası"
                                  className="sa-form-input"
                                  maxLength="10"
                                  style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }}
                                />
                              </div>

                              <div className="sa-form-group">
                                <label style={{ fontWeight: '600', color: '#334155', marginBottom: '8px', display: 'block' }}>E-Posta <span className="sa-required" style={{ color: '#ef4444' }}>*</span></label>
                                <input
                                  type="email"
                                  value={clubApplicationData.email}
                                  onChange={(e) => setClubApplicationData({ ...clubApplicationData, email: e.target.value })}
                                  placeholder="E-posta adresiniz"
                                  className="sa-form-input"
                                />
                              </div>

                              <div className="sa-form-group">
                                <label style={{ fontWeight: '600', color: '#334155', marginBottom: '8px', display: 'block' }}>Telefon <span className="sa-required" style={{ color: '#ef4444' }}>*</span></label>
                                <input
                                  type="tel"
                                  value={clubApplicationData.phone}
                                  onChange={(e) => setClubApplicationData({ ...clubApplicationData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                                  placeholder="5XXXXXXXXX"
                                  className="sa-form-input"
                                  maxLength="10"
                                  style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }}
                                />
                              </div>
                            </div>
                          </div>

                          {/* BÖLÜM 2: TESİS VE ZAMAN */}
                          <div>
                            <h4 style={{ fontSize: '0.85rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <span style={{ width: '24px', height: '3px', background: '#e2e8f0', borderRadius: '2px', display: 'block' }}></span>
                              Tesis ve Zaman Planlaması
                            </h4>

                            <div className="sa-form-group sa-form-full" style={{ marginBottom: '24px' }}>
                              <label style={{ fontWeight: '600', color: '#334155', marginBottom: '8px', display: 'block' }}>Bölge Seçin <span className="sa-required" style={{ color: '#ef4444' }}>*</span></label>
                              <select
                                value={clubApplicationData.regionId}
                                onChange={(e) => setClubApplicationData({ ...clubApplicationData, regionId: e.target.value })}
                                className="sa-form-input"
                              >
                                <option value="">Bölge seçiniz...</option>
                                {regions.map(region => (
                                  <option key={region.id} value={region.id}>
                                    {region.name}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="sa-club-info-box">
                              <p>
                                <strong>Bilgi:</strong> Antrenman günleri ve saatleri, başvurunuz onaylandıktan sonra yetkili personel tarafından tesis müsaitliğine göre belirlenecektir.
                              </p>
                            </div>
                          </div>

                          {clubApplicationError && (
                            <div className="sa-club-error" style={{ marginTop: '24px' }}>
                              <FaTimesCircle /> {clubApplicationError}
                            </div>
                          )}
                        </div>

                        <div className="sa-verification-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '32px', borderTop: '1px solid #f0f0f0', paddingTop: '24px' }}>
                          <button
                            className="sa-btn-cancel"
                            onClick={() => { setIsClubApplicationMode(false); setClubSchedule({}); }}
                            style={{ padding: '12px 24px', borderRadius: '10px', fontSize: '0.95rem' }}
                          >
                            Vazgeç
                          </button>
                          <button
                            className="sa-btn-verify"
                            onClick={handleClubApplication}
                            disabled={clubApplicationLoading}
                            style={{
                              padding: '12px 32px',
                              borderRadius: '10px',
                              fontSize: '0.95rem',
                              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)',
                              border: 'none',
                              color: '#fff',
                              fontWeight: '600'
                            }}
                          >
                            {clubApplicationLoading ? 'Gönderiliyor...' : 'Başvuruyu Tamamla'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : selectedFacility ? (
              <div className="sa-facility-details">


                <div className="sa-appointment-section">
                  {/* PROGRESS STEPPER */}
                  <div className="sa-stepper">
                    {['slots', 'details', 'confirmation', 'payment', '3dsecure'].map((step, index) => {
                      const stepNames = { slots: 'Zaman', details: 'Bilgiler', confirmation: 'Onay', payment: isCreditFacility ? 'Rezervasyon' : 'Ödeme', '3dsecure': 'Tamamla' };
                      const currentStepIndex = ['slots', 'details', 'confirmation', 'payment', '3dsecure'].indexOf(viewStep);
                      const isCompleted = index < currentStepIndex;
                      const isActive = index === currentStepIndex;

                      return (
                        <div
                          key={step}
                          className={`sa-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${index <= currentStepIndex ? 'clickable' : ''}`}
                          onClick={() => {
                            // Allow navigation to previous steps or current step
                            if (index <= currentStepIndex) {
                              setViewStep(step);
                            }
                          }}
                        >
                          <div className="sa-step-circle">
                            {isCompleted ? <FaCheckCircle /> : index + 1}
                          </div>
                          <span className="sa-step-label">{stepNames[step]}</span>
                          {index < 4 && <div className="sa-step-line" />}
                        </div>
                      );
                    })}
                  </div>

                  {/* STEP 1: SLOTS VIEW */}
                  {viewStep === 'slots' && (
                    <>
                      {/* Sport Type Selector for Multi-Sport Facilities */}
                      {selectedFacility.isSharedFacility && selectedFacility.availableSports?.length > 0 && (
                        <div className="sa-sport-selector-section" style={{ marginBottom: '1.5rem' }}>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1e293b' }}>
                            Spor Türü Seçin
                          </label>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' }}>
                            {selectedFacility.availableSports.map((sport) => (
                              <button
                                key={sport.typeId}
                                type="button"
                                onClick={() => {
                                  setSelectedSportType(sport);
                                  setSelectedTimeSlot(null);
                                }}
                                className={selectedSportType?.typeId === sport.typeId ? 'selected' : ''}
                                style={{
                                  padding: '0.75rem',
                                  border: selectedSportType?.typeId === sport.typeId ? '2px solid #237CB2' : '2px solid #e5e7eb',
                                  borderRadius: '12px',
                                  background: selectedSportType?.typeId === sport.typeId ? '#f0f9ff' : '#fff',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  textAlign: 'center',
                                  fontWeight: '600',
                                  fontSize: '0.9rem'
                                }}
                              >
                                <div style={{ color: '#1e293b', marginBottom: '4px' }}>{sport.typeName}</div>
                                <div style={{ color: '#237CB2', fontSize: '0.85rem' }}>{formatVatIncluded(sport.pricePerHour)} / saat</div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedFacility.allowClubReservations && (
                        <div className="sa-date-picker-section" style={{ marginBottom: '1.5rem', marginTop: '0', backgroundColor: '#f9fafb', padding: '1rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                          <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#1e293b' }}>
                            Rezervasyon Tipi
                          </h4>
                          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                            <button
                              type="button"
                              onClick={() => setReservationType('Individual')}
                              style={{
                                flex: 1,
                                padding: '0.75rem',
                                borderRadius: '8px',
                                border: reservationType === 'Individual' ? '2px solid #237CB2' : '1px solid #ddd',
                                backgroundColor: reservationType === 'Individual' ? '#f0f9ff' : 'white',
                                color: reservationType === 'Individual' ? '#237CB2' : '#666',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                            >
                              Bireysel
                            </button>
                            <button
                              type="button"
                              onClick={() => setReservationType('Club')}
                              style={{
                                flex: 1,
                                padding: '0.75rem',
                                borderRadius: '8px',
                                border: reservationType === 'Club' ? '2px solid #237CB2' : '1px solid #ddd',
                                backgroundColor: reservationType === 'Club' ? '#f0f9ff' : 'white',
                                color: reservationType === 'Club' ? '#237CB2' : '#666',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                            >
                              Kulüp / Takım
                            </button>
                          </div>

                          {reservationType === 'Club' && (
                            <div style={{ padding: '0.8rem', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #eee' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                  <label style={{ fontWeight: '600', color: '#444' }}>Kişi Sayısı:</label>
                                  <input
                                    type="number"
                                    min="1"
                                    max="50"
                                    value={personCount}
                                    onChange={(e) => {
                                      const val = parseInt(e.target.value);
                                      if (val > 0) setPersonCount(val);
                                      else if (e.target.value === '') setPersonCount('');
                                    }}
                                    style={{
                                      width: '80px',
                                      padding: '0.5rem',
                                      borderRadius: '6px',
                                      border: '1px solid #ccc',
                                      fontSize: '1rem',
                                      textAlign: 'center'
                                    }}
                                  />
                                </div>
                                <div style={{ fontSize: '0.9rem', color: '#555', backgroundColor: '#f0f9ff', padding: '0.5rem 1rem', borderRadius: '20px', border: '1px solid #bae6fd' }}>
                                  Kişi Başı: <strong>{formatVatIncluded(selectedFacility.clubPricePerPerson)}</strong>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="sa-date-picker-section">
                        <label>Tarih Seçin</label>
                        <div className="sa-date-buttons" style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                          <button
                            type="button"
                            className={`sa-date-btn ${selectedDate === turkeyToday ? 'active' : ''}`}
                            onClick={() => setSelectedDate(turkeyToday)}
                            style={{
                              flex: 1,
                              padding: '12px 16px',
                              borderRadius: '12px',
                              border: selectedDate === turkeyToday ? '2px solid #237CB2' : '2px solid #e2e8f0',
                              background: selectedDate === turkeyToday ? 'linear-gradient(135deg, #237CB2, #1a5a8a)' : '#fff',
                              color: selectedDate === turkeyToday ? '#fff' : '#334155',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center'
                            }}
                          >
                            <span style={{ fontWeight: '600', fontSize: '1rem' }}>
                              {getTurkeyLabelFromDateString(turkeyToday, { day: 'numeric', month: 'long' })}
                            </span>
                            <span style={{ fontSize: '0.85rem', opacity: 0.9, marginTop: '2px' }}>
                              {getTurkeyLabelFromDateString(turkeyToday, { weekday: 'long' })}
                            </span>
                          </button>
                          <button
                            type="button"
                            className={`sa-date-btn ${selectedDate === turkeyTomorrow ? 'active' : ''}`}
                            onClick={() => setSelectedDate(turkeyTomorrow)}
                            style={{
                              flex: 1,
                              padding: '12px 16px',
                              borderRadius: '12px',
                              border: selectedDate === turkeyTomorrow ? '2px solid #237CB2' : '2px solid #e2e8f0',
                              background: selectedDate === turkeyTomorrow ? 'linear-gradient(135deg, #237CB2, #1a5a8a)' : '#fff',
                              color: selectedDate === turkeyTomorrow ? '#fff' : '#334155',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center'
                            }}
                          >
                            <span style={{ fontWeight: '600', fontSize: '1rem' }}>
                              {getTurkeyLabelFromDateString(turkeyTomorrow, { day: 'numeric', month: 'long' })}
                            </span>
                            <span style={{ fontSize: '0.85rem', opacity: 0.9, marginTop: '2px' }}>
                              {getTurkeyLabelFromDateString(turkeyTomorrow, { weekday: 'long' })}
                            </span>
                          </button>
                        </div>
                      </div>

                      <div className="sa-time-slots-section" style={{ position: 'relative' }}>
                        <h4>Müsait Saatler <span className="sa-working-hours">({selectedFacility.workingHoursStart || '08:00'} - {selectedFacility.workingHoursEnd || '00:00'})</span></h4>

                        {isSlotsLoading && (
                          <div style={{
                            position: 'absolute',
                            top: '40px', left: 0, right: 0, bottom: 0,
                            background: 'rgba(255,255,255,0.7)',
                            zIndex: 10,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(2px)',
                            borderRadius: '12px'
                          }}>
                            <div className="sa-loading-spinner" style={{ borderTopColor: '#3b82f6', width: '30px', height: '30px' }}></div>
                            <span style={{ marginTop: '10px', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Saatler Güncelleniyor...</span>
                          </div>
                        )}

                        <div className="sa-time-slots-grid" style={{ opacity: isSlotsLoading ? 0.3 : 1, pointerEvents: isSlotsLoading ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
                          {(Array.isArray(selectedFacility.timeSlots) ? selectedFacility.timeSlots : []).map((slot, index) => {
                            // Check if slot is in the past (for today only)
                            const isToday = selectedDate === turkeyToday;
                            const { hour: currentHour, minute: currentMinute } = getTurkeyCurrentHourMinute();

                            // Extract start time from slot (handles "08:00" or "08:00 - 09:00" format)
                            const startTime = slot.time.split(' ')[0]; // Gets "08:00" from "08:00 - 09:00"
                            const [slotHour, slotMinute] = startTime.split(':').map(Number);

                            // Compare: slot is past if hour is less, or hour is equal but minute is less/equal
                            const isPastSlot = isToday && (slotHour < currentHour || (slotHour === currentHour && slotMinute <= currentMinute));

                            const isDisabled = !slot.available || isPastSlot;
                            const isClubSlot = (slot.blockSource || '').toLowerCase() === 'club';
                            const isMemberHour = (slot.blockSource || '').toLowerCase() === 'memberhour';
                            const isMinistryHour = (slot.blockSource || '').toLowerCase() === 'ministryhour';
                            const isBlockedSlot = isClubSlot || isMemberHour || isMinistryHour;
                            const genderClass = slot.allowedGender === 'Male' ? 'gender-male' : slot.allowedGender === 'Female' ? 'gender-female' : '';
                            const capacityLabel = getSlotCapacityLabel(slot);

                            // Custom styles for special hour types
                            let customStyle = {};
                            if (isPastSlot) {
                              customStyle = { opacity: 0.5, background: '#e5e7eb', cursor: 'not-allowed' };
                            } else if (isBlockedSlot) {
                              // All blocked/reserved slots shown as occupied (red) — no reason exposed
                              customStyle = { background: 'linear-gradient(135deg, #fef2f2, #fee2e2)', borderColor: '#fca5a5', color: '#991b1b', cursor: 'not-allowed', opacity: 0.7 };
                            }

                            return isMemberHour ? (
                              <div
                                key={index}
                                className={`sa-time-slot occupied`}
                                style={{ ...customStyle, position: 'relative' }}
                              >
                                <span className="sa-slot-time">{slot.time}</span>
                                {capacityLabel && (
                                  <span style={{ display: 'block', fontSize: '0.72rem', marginTop: '4px', fontWeight: '600', color: '#64748b' }}>
                                    {capacityLabel}
                                  </span>
                                )}
                                <span style={{ display: 'block', fontSize: '0.75rem', marginTop: '2px', fontWeight: '700', color: '#991b1b' }}>
                                  Dolu
                                </span>
                              </div>
                            ) : (
                              <button
                                key={index}
                                type="button"
                                className={`sa-time-slot ${isPastSlot ? 'past' : (slot.available ? 'available' : 'occupied')} ${genderClass} ${selectedTimeSlot?.time === slot.time ? 'selected' : ''}`}
                                disabled={isDisabled}
                                onClick={() => !isDisabled && handleTimeSlotSelected(slot)}
                                style={customStyle}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <span className="sa-slot-time">{slot.time}</span>
                                  {slot.allowedGender === 'Male' && <span style={{ color: '#3b82f6', fontSize: '1rem', fontWeight: 'bold' }}>♂</span>}
                                  {slot.allowedGender === 'Female' && <span style={{ color: '#ec4899', fontSize: '1rem', fontWeight: 'bold' }}>♀</span>}
                                </div>
                                {capacityLabel && (
                                  <span style={{ display: 'block', fontSize: '0.72rem', marginTop: '4px', fontWeight: '600', color: isDisabled ? '#94a3b8' : '#64748b' }}>
                                    {capacityLabel}
                                  </span>
                                )}
                                {isMinistryHour ? (
                                  <span style={{ display: 'block', fontSize: '0.75rem', marginTop: '2px', fontWeight: '700', color: '#991b1b' }}>
                                    Dolu
                                  </span>
                                ) : isClubSlot ? (
                                  <span style={{ display: 'block', fontSize: '0.75rem', marginTop: '2px', fontWeight: '700', color: '#991b1b' }}>
                                    Dolu
                                  </span>
                                ) : (
                                  <>
                                    <span className="sa-slot-price" style={{ display: 'block', fontSize: '0.8rem', marginTop: '2px', fontWeight: 'bold', color: isDisabled ? '#9ca3af' : '#237CB2' }}>
                                      {isPastSlot || !slot.available ? '' : formatVatIncluded(reservationType === 'Club'
                                        ? (() => {
                                            if (selectedFacility.clubNightStartTime && selectedFacility.clubNightPricePerPerson) {
                                              const slotTimeParts = startTime.split(':').map(Number);
                                              const nightTimeParts = selectedFacility.clubNightStartTime.split(':').map(Number);
                                              const isNight = slotTimeParts[0] > nightTimeParts[0] || (slotTimeParts[0] === nightTimeParts[0] && slotTimeParts[1] >= nightTimeParts[1]);
                                              const price = isNight ? selectedFacility.clubNightPricePerPerson : (selectedFacility.clubPricePerPerson || 0);
                                              return price ? price * (personCount || 1) : 0;
                                            }
                                            return selectedFacility.clubPricePerPerson ? selectedFacility.clubPricePerPerson * (personCount || 1) : 0;
                                          })()
                                        : slot.price)}
                                    </span>
                                    {slot.allowedGender && slot.allowedGender !== 'All' && slot.available && (
                                      <span style={{ display: 'block', fontSize: '0.7rem', marginTop: '1px', fontWeight: '600', color: slot.allowedGender === 'Male' ? '#2563eb' : '#db2777' }}>
                                        {slot.allowedGender === 'Male' ? 'Erkek Saati' : 'Kadın Saati'}
                                      </span>
                                    )}
                                    {!slot.available && !isPastSlot && (
                                      <span style={{ display: 'block', fontSize: '0.75rem', marginTop: '2px', fontWeight: '700', color: '#991b1b' }}>
                                        Dolu
                                      </span>
                                    )}
                                  </>
                                )}
                              </button>
                            );
                          })}
                        </div>

                        {showMemberInfoModal && createPortal(
                          <div
                            onClick={() => setShowMemberInfoModal(false)}
                            style={{
                              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                              background: 'rgba(0,0,0,0.5)',
                              zIndex: 9999,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              animation: 'saModalFadeIn 0.15s ease'
                            }}
                          >
                            <div
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                background: '#fff',
                                borderRadius: '20px',
                                padding: '2rem 2rem 1.5rem',
                                maxWidth: '380px',
                                width: '90%',
                                boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
                                textAlign: 'center',
                                animation: 'saModalSlideUp 0.2s ease'
                              }}
                            >
                              <div style={{
                                width: '56px', height: '56px', borderRadius: '50%',
                                background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                margin: '0 auto 1rem',
                                boxShadow: '0 4px 12px rgba(245,158,11,0.3)'
                              }}>
                                <FaInfoCircle style={{ fontSize: '1.5rem', color: '#d97706' }} />
                              </div>
                              <h3 style={{ margin: '0 0 0.5rem', color: '#1e293b', fontSize: '1.15rem', fontWeight: '700' }}>
                                Üye Saati
                              </h3>
                              <p style={{ margin: '0 0 1.5rem', color: '#64748b', fontSize: '0.9rem', lineHeight: '1.5' }}>
                                Bu saat dilimi tesise kayıtlı üyelere ayrılmıştır. Bu saatte tesisimizi kullanmak için lütfen <strong>üyelik başvurusu</strong> yapınız.
                              </p>
                              <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                  type="button"
                                  onClick={() => setShowMemberInfoModal(false)}
                                  style={{
                                    flex: 1, padding: '12px', borderRadius: '12px',
                                    border: '1.5px solid #e2e8f0', background: '#fff',
                                    color: '#64748b', fontWeight: '600', fontSize: '0.9rem',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Kapat
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowMemberInfoModal(false);
                                    setIsCardManagementMode(true);
                                    setIsCardApplicationMode(false);
                                    setIsBalanceLoadMode(false);
                                    setIsClubApplicationMode(false);
                                    setIsCancellationMode(false);
                                    setIsPublicEducationMode(false);
                                    setIsSummerSportsMode(false);
                                    setIsTennisAcademyMode(false);
                                    setIsClubPaymentMode(false);
                                    setSelectedFacility(null);
                                  }}
                                  style={{
                                    flex: 1, padding: '12px', borderRadius: '12px',
                                    border: 'none',
                                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                    color: '#fff', fontWeight: '700', fontSize: '0.9rem',
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 12px rgba(245,158,11,0.35)'
                                  }}
                                >
                                  <FaIdCard style={{ marginRight: '6px' }} /> Üyelik İşlemleri
                                </button>
                              </div>
                            </div>
                          </div>,
                          document.body
                        )}
                        <style>{`
                          @keyframes saModalFadeIn { from { opacity: 0; } to { opacity: 1; } }
                          @keyframes saModalSlideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
                        `}</style>

                        {selectedTimeSlot && (
                          <>
                            {selectedSlotCapacityLabel && (
                              <div className="sa-info-box" style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                                <p style={{ margin: 0, color: '#0369a1', fontSize: '0.95rem' }}>
                                  <strong>Seçilen Saat:</strong> {selectedTimeSlot.time}<br />
                                  {selectedSlotCapacityLabel}
                                </p>
                              </div>
                            )}
                            <div className="sa-action-row">
                              <button
                                className="sa-submit-button sa-btn-next"
                                onClick={() => setViewStep('details')}
                              >
                                Randevu Al <FaChevronRight />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  )}

                  {/* STEP 2: DETAILS VIEW */}
                  {/* STEP 2: DETAILS VIEW */}
                  {viewStep === 'details' && selectedTimeSlot && (
                    <div className="sa-booking-form" ref={formRef}>
                      <div className="sa-form-header">
                        <h4>Randevu Bilgileri</h4>
                        <button
                          className="sa-icon-back-button"
                          onClick={() => setViewStep('slots')}
                          title="Geri Dön"
                        >
                          <FaArrowLeft />
                        </button>
                      </div>

                      {reservationType === 'Club' && !isClubVerified ? (
                        <div className="sa-form-grid">
                          <div className="sa-info-box" style={{ gridColumn: '1 / -1', marginBottom: '1rem', padding: '1rem', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                            <p style={{ margin: 0, color: '#0369a1', fontSize: '0.95rem' }}>
                              <strong>Kulüp Rezervasyonu ?çin Doğrulama</strong><br />
                              Lütfen kulüp vergi numaranızı ve kayıtlı telefon numaranızı giriniz.
                            </p>
                          </div>

                          <div className="sa-form-group">
                            <label htmlFor="verifyTaxNumber">Vergi No <span className="sa-required">*</span></label>
                            <input
                              id="verifyTaxNumber"
                              type="text"
                              value={clubVerifyData.taxNumber}
                              onChange={(e) => setClubVerifyData({ ...clubVerifyData, taxNumber: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                              placeholder="Vergi Numarası"
                              maxLength="10"
                              className="sa-form-input"
                            />
                          </div>

                          <div className="sa-form-group">
                            <label htmlFor="verifyPhone">Telefon <span className="sa-required">*</span></label>
                            <input
                              id="verifyPhone"
                              type="tel"
                              value={clubVerifyData.phone}
                              onChange={(e) => { const value = e.target.value.replace(/\D/g, ''); if (value.length <= 10) setClubVerifyData({ ...clubVerifyData, phone: value }); }}
                              placeholder="5XXXXXXXXX"
                              className="sa-form-input"
                              maxLength="10"
                            />
                          </div>

                          <div className="sa-form-group" style={{ gridColumn: '1 / -1' }}>
                            <button
                              type="button"
                              className="sa-submit-button"
                              onClick={handleVerifyClub}
                              disabled={verifyingClub}
                              style={{ width: '100%', marginTop: '1rem' }}
                            >
                              {verifyingClub ? 'Doğrulanıyor...' : 'Kulüp Bilgilerini Doğrula'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="sa-form-grid">
                            {isFitnessFacility && (
                              <div className="sa-info-box" style={{ gridColumn: '1 / -1', marginBottom: '1rem', padding: '1.25rem', backgroundColor: '#f0f9ff', borderRadius: '12px', border: '1px solid #bae6fd' }}>
                                <p style={{ margin: 0, color: '#0369a1', fontSize: '0.98rem', lineHeight: '1.6' }}>
                                  <strong>Kartınız yoksa önce kart başvurusu yapmanız gerekir.</strong><br />
                                  Aktif kartı olan kullanıcılar randevu alma işlemine bu ekrandan devam edebilir.
                                </p>
                                <button
                                  type="button"
                                  className="sa-submit-button sa-btn-next"
                                  onClick={() => {
                                    setIsCardManagementMode(true);
                                    setCardManagementSubView('applications');
                                    setIsCardApplicationMode(false);
                                    setIsBalanceLoadMode(false);
                                    setIsClubApplicationMode(false);
                                    setIsCancellationMode(false);
                                    setIsPublicEducationMode(false);
                                    setIsSummerSportsMode(false);
                                    setIsTennisAcademyMode(false);
                                    setIsClubPaymentMode(false);
                                  }}
                                  style={{ marginTop: '1rem', width: 'auto', minWidth: '220px' }}
                                >
                                  Kart Başvurusuna Git <FaChevronRight />
                                </button>
                              </div>
                            )}
                            {!isCreditFacility && (
                            <>
                            <div className="sa-form-group">
                              <label htmlFor="firstName">Ad {!isCreditFacility && <span className="sa-required">*</span>}</label>
                              <input id="firstName" type="text" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} placeholder="Adınızı giriniz" className="sa-form-input" disabled={isClubVerified} />
                            </div>

                            <div className="sa-form-group">
                              <label htmlFor="lastName">Soyad {!isCreditFacility && <span className="sa-required">*</span>}</label>
                              <input id="lastName" type="text" value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} placeholder="Soyadınızı giriniz" className="sa-form-input" disabled={isClubVerified} />
                            </div>

                            <div className="sa-form-group">
                              <label htmlFor="userGender">Cinsiyet {!isCreditFacility && <span className="sa-required">*</span>}</label>
                              <select
                                id="userGender"
                                value={formData.userGender}
                                onChange={(e) => setFormData({ ...formData, userGender: e.target.value })}
                                className="sa-form-input"
                              >
                                <option value="">Seçiniz</option>
                                <option value="Male">Erkek</option>
                                <option value="Female">Kadın</option>
                                {reservationType === 'Club' && <option value="Mixed">Karma</option>}
                              </select>
                            </div>
                            </>
                            )}

                            <div className="sa-form-group">
                              <label htmlFor="idNumber">T.C. Kimlik No <span className="sa-required">*</span></label>
                              <input id="idNumber" type="text" value={formData.idNumber} onChange={(e) => setFormData({ ...formData, idNumber: e.target.value.replace(/\D/g, '').slice(0, 11) })} placeholder="TC Kimlik No giriniz" className="sa-form-input" maxLength="11" disabled={isClubVerified} />
                            </div>

                            {!isCreditFacility && (
                            <div className="sa-form-group">
                              <label htmlFor="email">E-Posta <span className="sa-required">*</span></label>
                              <input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="E-posta adresiniz" className="sa-form-input" disabled={isClubVerified} />
                            </div>
                            )}

                            <div className="sa-form-group">
                              <label htmlFor="phone">Telefon <span className="sa-required">*</span></label>
                              <input id="phone" type="tel" value={formData.phone} onChange={(e) => { const value = e.target.value.replace(/\D/g, ''); if (value.length <= 10) setFormData({ ...formData, phone: value }); }} placeholder="5XXXXXXXXX" className="sa-form-input" maxLength="10" disabled={isClubVerified} />
                            </div>

                            <div className="sa-form-group sa-appointment-summary">
                              <label>Randevu Saati</label>
                              <div className="sa-summary-box">
                                <strong>{selectedTimeSlot.time}</strong>
                                <span>{new Date(selectedDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                                {selectedSlotCapacityLabel && (
                                  <span style={{ marginTop: '4px', fontWeight: '600', color: '#237CB2' }}>
                                    {selectedSlotCapacityLabel}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>


                          {!isCreditFacility && (<><div className="sa-terms-checkbox">
                            <input type="checkbox" id="termsAccept" checked={termsAccepted} onChange={(e) => { if (!termsViewed) { e.preventDefault(); setShowTermsModal(true); } else { setTermsAccepted(e.target.checked); } }} onClick={(e) => { if (!termsViewed) { e.preventDefault(); setShowTermsModal(true); } }} />
                            <label htmlFor="termsAccept"><span className="sa-terms-link" onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}>{'Hizmet Şartları'}</span>'nı okudum ve kabul ediyorum</label>
                          </div>
                          <div className="sa-terms-checkbox">
                            <input type="checkbox" id="refundPolicyAccept" checked={refundPolicyAccepted} onChange={(e) => { if (!refundPolicyViewed) { e.preventDefault(); setShowRefundPolicyModal(true); } else { setRefundPolicyAccepted(e.target.checked); } }} onClick={(e) => { if (!refundPolicyViewed) { e.preventDefault(); setShowRefundPolicyModal(true); } }} />
                            <label htmlFor="refundPolicyAccept"><span className="sa-terms-link" onClick={(e) => { e.preventDefault(); setShowRefundPolicyModal(true); }}>Para İade Politikası</span>'nı okudum ve kabul ediyorum</label>
                          </div>
                          <div className="sa-terms-checkbox">
                            <input type="checkbox" id="contractAccept" checked={contractAccepted} onChange={(e) => { if (!contractViewed) { e.preventDefault(); setShowContractModal(true); } else { setContractAccepted(e.target.checked); } }} onClick={(e) => { if (!contractViewed) { e.preventDefault(); setShowContractModal(true); } }} />
                            <label htmlFor="contractAccept"><span className="sa-terms-link" onClick={(e) => { e.preventDefault(); setShowContractModal(true); }}>Mesafeli Satış Sözleşmesi</span>'ni okudum ve kabul ediyorum</label>
                          </div></>)}

                          <div className="sa-action-row">
                            <button
                              className="sa-submit-button sa-btn-next"
                              onClick={() => {
                                if (!formData.idNumber || !formData.phone || (!isCreditFacility && (!formData.firstName || !formData.lastName || !formData.email || !formData.userGender))) {
                                  toast.warn('Lütfen tüm zorunlu alanları doldurunuz.');
                                  return;
                                }

                                // Gender Restriction Check (Hourly - from slot data)
                                if (selectedTimeSlot?.allowedGender && selectedTimeSlot.allowedGender !== 'All' && formData.userGender) {
                                  if (selectedTimeSlot.allowedGender === 'Male' && formData.userGender !== 'Male') {
                                    toast.error('Bu saat sadece ERKEK kullanıcılara hizmet vermektedir.');
                                    return;
                                  }
                                  if (selectedTimeSlot.allowedGender === 'Female' && formData.userGender !== 'Female') {
                                    toast.error('Bu saat sadece KADIN kullanıcılara hizmet vermektedir.');
                                    return;
                                  }
                                }

                                setViewStep('confirmation');
                              }}
                              disabled={!isCreditFacility && (!termsAccepted || !refundPolicyAccepted || !contractAccepted)}
                            >
                              Devam Et <FaChevronRight />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* STEP 3: CONFIRMATION VIEW */}
                  {viewStep === 'confirmation' && selectedTimeSlot && (
                    <div className="sa-booking-form">
                      <div className="sa-form-header">
                        <div className="sa-form-header-text">
                          <h4>Ödeme Öncesi Son Kontrol</h4>
                          <h5>Bilgileri Onayla</h5>
                          <p>Randevu ve iletişim bilgilerinizi kontrol ederek güvenli ödeme adımına geçin.</p>
                        </div>
                        <button
                          className="sa-icon-back-button"
                          onClick={() => setViewStep('details')}
                          title="Geri Dön"
                        >
                          <FaArrowLeft />
                        </button>
                      </div>

                      <div className="sa-confirmation-hero">
                        <div className="sa-confirmation-hero-copy">
                          <span className="sa-confirmation-badge">Randevu Özeti</span>
                          <h5>{selectedFacility.name}</h5>
                          <p>{isCreditFacility ? 'Seans paketi uygunluğu kontrol edilip 1 seans kullanılarak rezervasyon oluşturulacaktır.' : `${selectedFacility.regionName} için seçilen randevu bilgileri ve kullanıcı detayları aşağıda özetlenmiştir.`}</p>
                        </div>
                        <div className="sa-confirmation-hero-amount">
                          <span>{isCreditFacility ? 'Kullanılacak Hak' : 'Tahsil Edilecek Tutar'}</span>
                          <strong>{isCreditFacility ? '1 Seans' : formatCurrency(paymentTotal)}</strong>
                        </div>

                        <div className="sa-confirmation-meta-grid">
                          <div className="sa-confirmation-meta-card">
                            <span className="sa-confirmation-meta-icon">
                              <FaMapMarkerAlt />
                            </span>
                            <div>
                              <small>Tesis ve Bölge</small>
                              <strong>{selectedFacility.name}</strong>
                              <span>{selectedFacility.regionName}</span>
                            </div>
                          </div>
                          <div className="sa-confirmation-meta-card">
                            <span className="sa-confirmation-meta-icon">
                              <FaCalendarDay />
                            </span>
                            <div>
                              <small>Randevu Tarihi</small>
                              <strong>{new Date(selectedDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                              <span>Takvim seçiminiz hazır</span>
                            </div>
                          </div>
                          <div className="sa-confirmation-meta-card">
                            <span className="sa-confirmation-meta-icon">
                              <FaClock />
                            </span>
                            <div>
                              <small>Saat Aralığı</small>
                              <strong>{selectedTimeSlot.time}</strong>
                              <span>{selectedSlotCapacityLabel || (isCreditFacility ? '1 seans kullanılarak onaylanacak' : 'Ödeme sonrası aktifleşecek')}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="sa-confirmation-hero sa-confirmation-hero--secondary">
                        <div className="sa-confirmation-panel-header">
                          <div>
                            <h5>{isCreditFacility ? 'Kart Doğrulama Bilgileri' : 'Başvuru Bilgileri'}</h5>
                            <p>{isCreditFacility ? 'Aktif kartınız TC kimlik ve telefon bilginizle doğrulanır.' : 'Randevu sahibi iletişim ve profil bilgileri'}</p>
                          </div>
                        </div>
                        <div className="sa-confirmation-details">
                          {!isCreditFacility && <div className="sa-confirmation-row">
                            <span className="sa-label">Ad Soyad:</span>
                            <span className="sa-value">{formData.firstName} {formData.lastName}</span>
                          </div>}
                          <div className="sa-confirmation-row">
                            <span className="sa-label">TC Kimlik:</span>
                            <span className="sa-value">{formData.idNumber}</span>
                          </div>
                          {!isCreditFacility && <div className="sa-confirmation-row">
                            <span className="sa-label">Email:</span>
                            <span className="sa-value">{formData.email}</span>
                          </div>}
                          <div className="sa-confirmation-row">
                            <span className="sa-label">Telefon:</span>
                            <span className="sa-value">{formData.phone}</span>
                          </div>
                        </div>
                      </div>

                      <div className="sa-action-row">
                        <button className="sa-submit-button sa-btn-next" onClick={handleProceedFromConfirmation}>
                          {isCreditFacility ? 'Randevuyu Oluştur' : 'Ödemeye Geç'} <FaChevronRight />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* STEP 4: PAYMENT / 3D RESULT VIEW */}
                  {(viewStep === 'payment' || viewStep === '3dsecure') && (
                    <div className="sa-booking-form">
                      <div className="sa-form-header sa-form-header--payment">
                        <div className="sa-form-header-left">
                          {paymentOutcome !== 'success' && (
                            <button
                              className="sa-icon-back-button"
                              onClick={() => setViewStep('confirmation')}
                              title="Geri Dön"
                            >
                              <FaArrowLeft />
                            </button>
                          )}
                          <div className="sa-form-header-text">
                            <h4>{paymentOutcome === 'success' ? 'İşlem Sonucu' : 'Güvenli Ödeme Adımı'}</h4>
                            <h5>{paymentOutcome === 'success' ? 'Randevu Özeti' : 'Kart Bilgileri'}</h5>
                            <p>{paymentOutcome === 'success' ? 'Ödeme ve randevu durumunuz.' : 'Ödemenizi tamamlamak için kart bilgilerinizi giriniz.'}</p>
                          </div>
                        </div>
                        <div className="sa-payment-total-box">
                          <span>Ödenecek Tutar</span>
                          <strong>{formatVatIncluded(paymentTotal)}</strong>
                        </div>
                      </div>

                      {paymentOutcome ? (
                        <div className={`sa-payment-result ${paymentOutcome === 'success' ? 'sa-payment-success' : 'sa-payment-failed'}`}>
                          <div className={`sa-result-icon ${paymentOutcome === 'failed' ? 'error' : ''}`}>
                            {paymentOutcome === 'failed' ? <FaTimesCircle /> : <FaCheckCircle />}
                          </div>
                          <h2>{paymentOutcome === 'success' ? 'Ödeme Başarılı!' : 'Ödeme Başarısız'}</h2>
                          <p className={`sa-result-message ${paymentOutcome === 'failed' ? 'error' : ''}`}>
                            {paymentOutcome === 'success'
                              ? (fitnessReservationResult?.message || 'Randevunuz başarıyla oluşturuldu.')
                              : (paymentError || (isCreditFacility ? 'Seanslı randevu tamamlanamadı. Lütfen tekrar deneyin.' : 'Ödeme işlemi tamamlanamadı. Lütfen tekrar deneyin.'))}
                          </p>

                          {paymentOutcome === 'success' && (
                            <div className="sa-appointment-summary-box">
                              <h3>Randevu Bilgileri</h3>
                              {appointmentDetailsLoading ? (
                                <p>Randevu bilgileri yükleniyor...</p>
                              ) : appointmentDetails ? (
                                <>
                                  <div className="sa-summary-row">
                                    <span className="sa-summary-label">Ad Soyad</span>
                                    <span className="sa-summary-value">{appointmentDetails.firstName} {appointmentDetails.lastName}</span>
                                  </div>
                                  <div className="sa-summary-row">
                                    <span className="sa-summary-label">Tesis</span>
                                    <span className="sa-summary-value">{appointmentDetails.facilityName}</span>
                                  </div>
                                  <div className="sa-summary-row">
                                    <span className="sa-summary-label">Bölge</span>
                                    <span className="sa-summary-value">{appointmentDetails.regionName}</span>
                                  </div>
                                  <div className="sa-summary-row">
                                    <span className="sa-summary-label">Tarih</span>
                                    <span className="sa-summary-value">{new Date(appointmentDetails.appointmentDate).toLocaleDateString('tr-TR')}</span>
                                  </div>
                                  <div className="sa-summary-row">
                                    <span className="sa-summary-label">Saat</span>
                                    <span className="sa-summary-value">{appointmentDetails.startTime} - {appointmentDetails.endTime}</span>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="sa-summary-row">
                                    <span className="sa-summary-label">Ad Soyad</span>
                                    <span className="sa-summary-value">{formData.firstName} {formData.lastName}</span>
                                  </div>
                                  <div className="sa-summary-row">
                                    <span className="sa-summary-label">Tesis</span>
                                    <span className="sa-summary-value">{selectedFacility?.name || '-'}</span>
                                  </div>
                                  <div className="sa-summary-row">
                                    <span className="sa-summary-label">Tarih</span>
                                    <span className="sa-summary-value">{selectedDate ? new Date(selectedDate).toLocaleDateString('tr-TR') : '-'}</span>
                                  </div>
                                  <div className="sa-summary-row">
                                    <span className="sa-summary-label">Saat</span>
                                    <span className="sa-summary-value">{selectedTimeSlot?.time || '-'}</span>
                                  </div>
                                </>
                              )}
                              <div className="sa-summary-row highlight">
                                <span className="sa-summary-label">{isCreditFacility ? 'Kalan Seans' : 'Toplam Tutar'}</span>
                                <span className="sa-summary-value">{isCreditFacility ? (fitnessReservationResult?.remainingCredits ?? '-') : formatVatIncluded(paymentTotal)}</span>
                              </div>
                            </div>
                          )}

                          {paymentOutcome === 'success' && paymentResultData?.transactionId && (
                            <div className="sa-transaction-info">
                              <p className="sa-transaction-id">İşlem No: {paymentResultData.transactionId}</p>
                            </div>
                          )}

                          <div className="sa-action-row">
                            {paymentOutcome === 'failed' ? (
                              <>
                                <button
                                  className="sa-payment-action"
                                  data-variant="positive"
                                  onClick={() => {
                                    setViewStep('payment');
                                    setPaymentOutcome(null);
                                    setPaymentError(null);
                                  }}
                                >
                                  Tekrar Dene
                                </button>
                                <button
                                  className="sa-payment-action"
                                  data-variant="danger"
                                  onClick={() => window.location.href = '/hizmet-rehberi/spor-randevu'}
                                  style={{ marginLeft: '12px' }}
                                >
                                  İptal Et
                                </button>
                              </>
                            ) : (
                              <button
                                className="sa-submit-button sa-btn-next"
                                onClick={() => window.location.href = '/hizmet-rehberi/spor-randevu'}
                              >
                                Ana Sayfaya Dön
                              </button>
                            )}
                          </div>
                        </div>
                      ) : paymentHtml ? (
                        <div className="sa-3dsecure-iframe" style={{ width: '100%', height: '600px', border: (isClubApplicationMode || isCancellationMode || isCardApplicationMode || isBalanceLoadMode || isPublicEducationMode || isSummerSportsMode || isTennisAcademyMode || isCardManagementMode) ? 'none' : '1px solid #e2e8f0', borderRadius: '12px' }}>
                          <iframe
                            title="3D Secure Payment"
                            srcDoc={paymentHtml}
                            ref={paymentIframeRef}
                            onLoad={handlePaymentIframeLoad}
                            style={{ width: '100%', height: '100%', border: 'none' }}
                          />
                        </div>
                      ) : (
                        <>
                          <div className="sa-modern-payment-shell">
                            <div className="sa-modern-payment-meta">
                              <div className="sa-modern-payment-meta-item">
                                <span className="sa-modern-payment-meta-label">Randevu Sahibi</span>
                                <strong className="sa-modern-payment-meta-value">{formData.firstName} {formData.lastName}</strong>
                              </div>
                              <div className="sa-modern-payment-meta-item sa-modern-payment-meta-item--amount">
                                <span className="sa-modern-payment-meta-label">Odenecek Tutar</span>
                                <strong className="sa-modern-payment-meta-value">{formatVatIncluded(paymentTotal)}</strong>
                              </div>
                            </div>

                            <div className="sa-modern-payment-layout">
                              <div className="sa-modern-card-preview">
                                <span className="sa-modern-card-chip" aria-hidden="true"></span>
                                <div className="sa-modern-card-number">{previewCardNumber}</div>
                                <div className="sa-modern-card-footer">
                                  <div>
                                    <span>Kart Sahibi</span>
                                    <strong>{previewCardHolder}</strong>
                                  </div>
                                  <div className="sa-modern-card-expiry">
                                    <span>SKT</span>
                                    <strong>{previewExpiry}</strong>
                                  </div>
                                </div>
                                <div className="sa-modern-card-brand">
                                  {detectCardType(paymentData.cardNumber) === '1' && <FaCcVisa size={28} color="#f8fafc" />}
                                  {detectCardType(paymentData.cardNumber) === '2' && <FaCcMastercard size={28} color="#fed7aa" />}
                                  {!detectCardType(paymentData.cardNumber) && <FaCreditCard size={24} color="#dbeafe" />}
                                </div>
                              </div>

                              <div className="sa-modern-payment-form">
                                <div className="sa-form-grid sa-modern-payment-grid">
                                  <div className="sa-form-group sa-modern-payment-wide">
                                    <label htmlFor="cardNumber">Kart Numarasi</label>
                                    <div className="sa-input-with-icon">
                                      <input
                                        id="cardNumber"
                                        type="text"
                                        value={paymentData.cardNumber}
                                        onChange={(e) => {
                                          const value = e.target.value.replace(/\D/g, '');
                                          const formatted = value.match(/.{1,4}/g)?.join(' ') || value;
                                          if (value.length <= 16) {
                                            setPaymentData({ ...paymentData, cardNumber: formatted });
                                            if (paymentErrors.cardNumber) setPaymentErrors({ ...paymentErrors, cardNumber: '' });
                                          }
                                        }}
                                        placeholder="0000 0000 0000 0000"
                                        className={`sa-form-input ${paymentErrors.cardNumber ? 'error' : ''}`}
                                        maxLength="19"
                                        inputMode="numeric"
                                      />
                                      <div className="sa-card-brand-icon">
                                        {detectCardType(paymentData.cardNumber) === '1' && <FaCcVisa size={24} color="#1a1f71" />}
                                        {detectCardType(paymentData.cardNumber) === '2' && <FaCcMastercard size={24} color="#f97316" />}
                                        {!detectCardType(paymentData.cardNumber) && <FaCreditCard size={22} color="#94a3b8" />}
                                      </div>
                                    </div>
                                    {paymentErrors.cardNumber && <div className="sa-error-message">{paymentErrors.cardNumber}</div>}
                                  </div>

                                  <div className="sa-form-group sa-modern-payment-wide">
                                    <label htmlFor="cardName">Kart Uzerindeki Isim</label>
                                    <input
                                      id="cardName"
                                      type="text"
                                      value={paymentData.cardName}
                                      onChange={(e) => {
                                        setPaymentData({ ...paymentData, cardName: e.target.value.toUpperCase() });
                                        if (paymentErrors.cardName) setPaymentErrors({ ...paymentErrors, cardName: '' });
                                      }}
                                      placeholder="AD SOYAD"
                                      className={`sa-form-input ${paymentErrors.cardName ? 'error' : ''}`}
                                    />
                                    {paymentErrors.cardName && <div className="sa-error-message">{paymentErrors.cardName}</div>}
                                  </div>

                                  <div className="sa-form-group">
                                    <label htmlFor="expiryMonth">Ay</label>
                                    <select
                                      id="expiryMonth"
                                      value={paymentData.expiryMonth}
                                      onChange={(e) => {
                                        setPaymentData({ ...paymentData, expiryMonth: e.target.value });
                                        if (paymentErrors.expiry) setPaymentErrors({ ...paymentErrors, expiry: '' });
                                      }}
                                      className={`sa-form-input ${paymentErrors.expiry ? 'error' : ''}`}
                                    >
                                      <option value="">Ay</option>
                                      {Array.from({ length: 12 }, (_, i) => {
                                        const month = String(i + 1).padStart(2, '0');
                                        return <option key={month} value={month}>{month}</option>;
                                      })}
                                    </select>
                                  </div>

                                  <div className="sa-form-group">
                                    <label htmlFor="expiryYear">Yil</label>
                                    <select
                                      id="expiryYear"
                                      value={paymentData.expiryYear}
                                      onChange={(e) => {
                                        setPaymentData({ ...paymentData, expiryYear: e.target.value });
                                        if (paymentErrors.expiry) setPaymentErrors({ ...paymentErrors, expiry: '' });
                                      }}
                                      className={`sa-form-input ${paymentErrors.expiry ? 'error' : ''}`}
                                    >
                                      <option value="">Yil</option>
                                      {Array.from({ length: 45 }, (_, i) => {
                                        const year = 2026 + i;
                                        return <option key={year} value={year}>{year}</option>;
                                      })}
                                    </select>
                                  </div>

                                  <div className="sa-form-group">
                                    <label htmlFor="cvv">CVV</label>
                                    <input
                                      id="cvv"
                                      type="text"
                                      value={paymentData.cvv}
                                      onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '');
                                        if (value.length <= 3) {
                                          setPaymentData({ ...paymentData, cvv: value });
                                          if (paymentErrors.cvv) setPaymentErrors({ ...paymentErrors, cvv: '' });
                                        }
                                      }}
                                      placeholder="123"
                                      className={`sa-form-input ${paymentErrors.cvv ? 'error' : ''}`}
                                      maxLength="3"
                                      inputMode="numeric"
                                    />
                                    {paymentErrors.cvv && <div className="sa-error-message">{paymentErrors.cvv}</div>}
                                  </div>
                                </div>

                                {paymentErrors.expiry && <div className="sa-error-message">{paymentErrors.expiry}</div>}
                              </div>
                            </div>
                          </div>
                          <div className="sa-action-row">
                            <button type="button" className="sa-submit-button sa-btn-next" onClick={handlePayment}>Ödemeyi Tamamla <FaCheckCircle style={{ marginLeft: 8 }} /></button>
                          </div>
                        </>
                      )}
                    </div>
                  )}


                </div>
              </div>
            ) : (
              <div className="sa-no-selection">
                <FaFutbol className="sa-no-selection-icon" />
                <h3>Saha Seçin</h3>
                <p>Randevu almak için sol panelden bölge ve saha seçiniz</p>
              </div>
            )}
          </div>
        </div>

        {
          showProgramConfirmModal && (
            <div className="sa-verification-modal-overlay" onClick={() => setShowProgramConfirmModal(false)}>
              <div className="sa-verification-modal" onClick={(e) => e.stopPropagation()}>
                <div className="sa-modal-header">
                  <h3>Spor Üyeliği Onayı</h3>
                  <button
                    className="sa-modal-close"
                    onClick={() => setShowProgramConfirmModal(false)}
                  >
                    &times;
                  </button>
                </div>
                <div className="sa-modal-body">
                  <p className="sa-verification-instruction">
                    Aldığınız randevu <strong>{getAppointmentProgramLabel()}</strong> üyeliğine aittir.
                  </p>
                  <p className="sa-verification-instruction">
                    Üyelik aktarımı yapılmamaktadır. Devam etmek istediğinizden emin misiniz?
                  </p>
                  <div className="sa-verification-actions">
                    <button className="sa-btn-verify" onClick={confirmProceedFromConfirmation}>
                      Eminim, Devam Et
                    </button>
                    <button className="sa-btn-cancel" onClick={() => setShowProgramConfirmModal(false)}>
                      Vazgeç
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        }

        {
          showVerificationModal && (
            <div className="sa-verification-modal-overlay" onClick={() => !isVerifying && setShowVerificationModal(false)}>
              <div className="sa-verification-modal" onClick={(e) => e.stopPropagation()}>
                <div className="sa-modal-header">
                  <h3>Email Doğrulama</h3>
                  <button
                    className="sa-modal-close"
                    onClick={() => setShowVerificationModal(false)}
                    disabled={isVerifying}
                  >
                    &times;
                  </button>
                </div>
                <div className="sa-modal-body">
                  <p className="sa-verification-instruction">
                    E-posta adresinize gönderilen 6 haneli doğrulama kodunu giriniz:
                  </p>
                  <input
                    type="text"
                    className="sa-verification-input"
                    maxLength="6"
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setVerificationCode(value);
                      if (verificationError) setVerificationError('');
                    }}
                    placeholder="000000"
                    disabled={isVerifying}
                    autoFocus
                  />
                  {verificationError && (
                    <div className="sa-verification-error">{verificationError}</div>
                  )}
                  <div className="sa-verification-actions">
                    <button
                      className="sa-btn-verify"
                      onClick={handleVerifyCode}
                      disabled={isVerifying || verificationCode.length !== 6}
                    >
                      {isVerifying ? 'Doğrulanıyor...' : 'Onayla'}
                    </button>
                    <button
                      className="sa-btn-cancel"
                      onClick={() => setShowVerificationModal(false)}
                      disabled={isVerifying}
                    >
                      İptal
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        }

        {
          showSuccessModal && successMessage && (
            <div className="sa-verification-modal-overlay" onClick={() => setShowSuccessModal(false)}>
              <div className="sa-verification-modal sa-success-modal" onClick={(e) => e.stopPropagation()}>
                <div className="sa-modal-body">
                  <div className="sa-success-icon">
                    <FaCheckCircle />
                  </div>
                  <h3>Randevunuz Onaylandı!</h3>
                  <p>Bilgileriniz mail üzerinden gönderilmiştir.</p>

                  <div className="sa-success-details">
                    <div className="sa-detail-row">
                      <span className="label">Ad Soyad:</span>
                      <span className="value">{successMessage.firstName} {successMessage.lastName}</span>
                    </div>
                    <div className="sa-detail-row">
                      <span className="label">Saha:</span>
                      <span className="value">{successMessage.facilityName}</span>
                    </div>
                    <div className="sa-detail-row">
                      <span className="label">Tarih:</span>
                      <span className="value">{successMessage.date}</span>
                    </div>
                    <div className="sa-detail-row">
                      <span className="label">Saat:</span>
                      <span className="value">{successMessage.time}</span>
                    </div>
                  </div>

                  <div className="sa-verification-actions">
                    <button
                      className="sa-btn-verify"
                      onClick={() => setShowSuccessModal(false)}
                    >
                      Tamam
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        }


        {
          showTermsModal && (
            <div className="sa-verification-modal-overlay" onClick={() => {
              setShowTermsModal(false);
              setTermsViewed(true);
            }}>
              <div className="sa-verification-modal sa-terms-modal" onClick={(e) => e.stopPropagation()}>
                <div className="sa-modal-header">
                  <h3>{'Hizmet Şartları'}</h3>
                  <button
                    className="sa-modal-close"
                    onClick={() => {
                      setShowTermsModal(false);
                      setTermsViewed(true);
                    }}
                  >
                    &times;
                  </button>
                </div>
                <TermsOfService />
                <div className="sa-verification-actions">
                  <button
                    className="sa-btn-verify"
                    onClick={() => {
                      setTermsAccepted(true);
                      setTermsViewed(true);
                      setShowTermsModal(false);
                    }}
                  >
                    Onaylıyorum
                  </button>
                </div>
              </div>
            </div>
          )
        }

        {
          showRefundPolicyModal && (
            <div className="sa-verification-modal-overlay" onClick={() => {
              setShowRefundPolicyModal(false);
              setRefundPolicyViewed(true);
            }}>
              <div className="sa-verification-modal sa-terms-modal" onClick={(e) => e.stopPropagation()}>
                <div className="sa-modal-header">
                  <h3>Para İade Politikası</h3>
                  <button
                    className="sa-modal-close"
                    onClick={() => {
                      setShowRefundPolicyModal(false);
                      setRefundPolicyViewed(true);
                    }}
                  >
                    &times;
                  </button>
                </div>
                <RefundPolicy />
                <div className="sa-verification-actions">
                  <button
                    className="sa-btn-verify"
                    onClick={() => {
                      setRefundPolicyAccepted(true);
                      setRefundPolicyViewed(true);
                      setShowRefundPolicyModal(false);
                    }}
                  >
                    Onaylıyorum
                  </button>
                </div>
              </div>
            </div>
          )
        }

        {
          showContractModal && (
            <div className="sa-verification-modal-overlay" onClick={() => {
              setShowContractModal(false);
              setContractViewed(true);
            }}>
              <div className="sa-verification-modal sa-terms-modal" onClick={(e) => e.stopPropagation()}>
                <div className="sa-modal-header">
                  <h3>Mesafeli Sat?ş Sözleşmesi</h3>
                  <button
                    className="sa-modal-close"
                    onClick={() => {
                      setShowContractModal(false);
                      setContractViewed(true);
                    }}
                  >
                    &times;
                  </button>
                </div>
                <DistanceSalesContract />
                <div className="sa-verification-actions">
                  <button
                    className="sa-btn-verify"
                    onClick={() => {
                      setContractAccepted(true);
                      setContractViewed(true);
                      setShowContractModal(false);
                    }}
                  >
                    Onaylıyorum
                  </button>
                </div>
              </div>
            </div>
          )
        }


      </div >
    </div >
  );
};

export default SportsAppointment;
